import {
	WORM_COLORS,
	DEFAULT_WORM_SPEED,
	TURN_RATE_NORMAL,
	TURN_RATE_BOOSTING,
	TURN_RATE_AGILITY,
	BOOST_SPEED_MULTIPLIER,
	DEFAULT_SEGMENT_COUNT,
	calculateWormRadius,
	MathUtils,
	AI_CHARACTERS,
	AI_PERSONALITIES,
	AIState,
	DEFAULT_AI_CONFIG,
	type AIPersonality,
	FOOD_RADIUS,
	PowerupType,
	POWERUP_CONFIGS,
	POWERUP_SYSTEM,
	MAGNET_RADIUS,
	MAGNET_HEAD_SCALE,
	MAGNET_PULL_STRENGTH,
	SLOW_MODE,
	MessageType,
	GROWTH,
	FREE_SPRINT_SCORE_THRESHOLD,
	FEAST_SYSTEM,
	FeastTier,
	type SkinData,
	type KillCounts,
	createEmptyKillCounts,
	calculateEarnedPoints,
	type TestModeConfig,
	MapShape,
	ShrinkingPhase,
	createConsoleLogger,
	type Logger,
} from '@wooorms/shared';
import { Container, Graphics } from 'pixi.js';
import {
	MINIMAP_SIZE_PERCENT,
	LEADERBOARD_MAX_ENTRIES,
	LEADERBOARD_PANEL_WIDTH,
	CAMERA_ZOOM_DELTA,
	WORM_GLOW_INTENSITY_DEFAULT,
	WORM_GLOW_INTENSITY_ONLINE,
	INPUT_SEND_RATE,
	BOOST_MOUSE_BUTTON,
	BASE_FOOD_PICKUP_OFFSET,
	EATING_FORWARD_OFFSET,
	COMBO_TIME_WINDOW,
	COMBO_MIN_KILLS,
	COMBO_POPUP_Y_OFFSET,
	EssenceType,
	ESSENCE_SMALL_VALUE,
	ESSENCE_MEDIUM_VALUE,
	ESSENCE_LARGE_VALUE,
	ESSENCE_MEDIUM_INTERVAL,
	ESSENCE_LARGE_INTERVAL,
	ESSENCE_SMALL_COLOR,
	ESSENCE_MEDIUM_COLOR,
	ESSENCE_LARGE_COLOR,
	ESSENCE_SMALL_SIZE,
	ESSENCE_MEDIUM_SIZE,
	ESSENCE_LARGE_SIZE,
} from './constants';
import {
	OFFLINE_PLAYER_SEGMENT_COUNT,
	OFFLINE_PLAYER_SEGMENT_SPACING,
	OFFLINE_AI_WORM_COUNT,
	OFFLINE_AI_TARGET_COUNT,
	OFFLINE_AI_MIN_COUNT,
	OFFLINE_AI_RESPAWN_INTERVAL,
	OFFLINE_AI_MIN_SEGMENTS,
	OFFLINE_AI_RANDOM_SEGMENTS,
	OFFLINE_AI_MIN_RADIUS,
	OFFLINE_AI_RANDOM_RADIUS,
	OFFLINE_AI_SEGMENT_SPACING,
	OFFLINE_AI_MIN_SPAWN_DISTANCE,
	OFFLINE_AI_RANDOM_SPAWN_DISTANCE,
	OFFLINE_FOOD_SPAWN_RADIUS_PERCENT,
	OFFLINE_POWERUP_SPAWN_RADIUS_PERCENT,
	OFFLINE_WORLD_RADIUS,
	calculateFoodCount,
	calculatePowerupCount,
	AI_WORM_SPEED,
	AI_BRAIN_THINK_INTERVAL_MS,
	AI_BRAIN_TURN_RATE,
	AI_BRAIN_BOOST_MULTIPLIER,
	SPRINT_MULTIPLIER,
	SLOW_CRAWL,
} from './offline-config';
import {
	AIBrainClient,
	LeviathanBrain,
	type ClientAIWormData,
	type ClientAIGameState,
	type ClientAIFoodData,
	type ClientAIPowerUpData,
} from './ai';
import { FogOfWar } from './game/systems/FogOfWar';
import { DeathEffectSystem } from './game/systems/DeathEffectSystem';
import { ParticleSystem } from './game/effects/ParticleSystem';
import { TextPopupSystem } from './game/effects/TextPopupSystem';
import { NearMissSystem } from './game/effects/NearMissSystem';
import { RevengeIntelArrow } from './game/ui/RevengeIntelArrow';
import { TrailSystem } from './game/effects/TrailSystem';
import { Game, GameLayer } from './game/Game';
import { WormRenderer, AccessoryType } from './game/entities/WormRenderer';
import { FoodRenderer } from './game/entities/FoodRenderer';
import { PowerupRenderer, type PowerupItem } from './game/entities/PowerupRenderer';
import type { ActivePowerup } from './ui/PowerupBar';
import { NetworkManager, ConnectionState, StateSync, InputManager, type KillType } from './network';
import {
	ConnectionUI,
	LeaderboardUI,
	DeathUI,
	EscMenu,
	Minimap,
	Leaderboard,
	PowerupBar,
	ScoreDisplay,
	KillScoreDisplay,
	FuelBar,
	ControlHints,
	MobileControls,
	VersionDisplay,
} from './ui';
import { OfflineGameMode } from './ui/ConnectionUI';
import { SprintGauge, getSprintTier } from './ui/SprintGauge';
import type { MinimapWorm, MinimapFood } from './ui';
import { SoundManager, SoundType } from './audio/SoundManager';
import { PerformanceMonitor } from './utils/PerformanceMonitor';
import { MobileSettings } from './utils/MobileSettings';

/**
 * Game mode - online or offline
 */
enum GameMode {
	MENU = 'menu',
	ONLINE = 'online',
	OFFLINE = 'offline',
}

/**
 * Test game state for offline demonstration
 */
interface OfflineGameState {
	/** Local player worm */
	playerWorm: WormRenderer;
	/** Other test worms */
	otherWorms: WormRenderer[];
	/** Food renderer */
	foodRenderer: FoodRenderer;
	/** Powerup renderer */
	powerupRenderer: PowerupRenderer;
	/** Fog of war system */
	fogOfWar: FogOfWar;
	/** Death effect system */
	deathEffects: DeathEffectSystem;
	/** Particle system for visual effects */
	particleSystem: ParticleSystem;
	/** Graphics for eating radius indicator */
	eatingRadiusGraphics: Graphics;
	/** Current mouse/touch position in world coordinates */
	mouseWorldPos: { x: number; y: number };
	/** Current mouse/touch position in screen coordinates (for recalculating world pos each frame) */
	mouseScreenPos: { x: number; y: number };
	/** Player worm speed */
	speed: number;
	/** Whether boost is active */
	boosting: boolean;
	/** Current fuel level (0-100) */
	fuel: number;
	/** Accumulator for boost segment burn (drops food when full) */
	boostBurnAccumulator: number;
	/** Player score (offline tracking) */
	score: number;
	/** Current worm facing angle (radians) - used for turn rate limiting */
	currentAngle: number;
	/** Player segment count (for growth tracking) */
	segmentCount: number;
	/** Whether player is dead */
	playerDead: boolean;
	/** Index of AI that killed the player (for revenge intel) */
	lastKillerIndex: number | null;
	/** AI worm names */
	aiNames: string[];
	/** AI worm segment counts */
	aiSegmentCounts: number[];
	/** Active powerups with remaining duration */
	activePowerups: Map<PowerupType, ActivePowerup>;
	/** Next powerup ID counter */
	nextPowerupId: number;
	/** Paused Final Slot (6x) when override (10x/15x) is active */
	paused6x: { remainingTime: number; totalDuration: number } | null;
	/** Active override type (10x or 15x) - null when not overriding */
	activeOverride: PowerupType.MULTIPLIER_10X | PowerupType.MULTIPLIER_15X | null;
	/** Speed bonus from speed powerups (0-3 stacks) */
	speedStacks: number;
	/** Magnet effect active */
	magnetActive: boolean;
	/** Agility effect active (better turn rate) */
	agilityActive: boolean;
	/** Vision effect active (increased visibility range) */
	visionActive: boolean;
	/** Slow mode timer (time spent not boosting) */
	slowModeTimer: number;
	/** Slow mode score accumulator (for fractional points) */
	slowModeScoreAccumulator: number;
	/** Growth accumulator (2 points = 1 segment) */
	growthAccumulator: number;
	/** AI worms currently in death animation (not yet removed) */
	dyingWorms: WormRenderer[];
	/** Timer for AI worm respawn checking */
	aiRespawnTimer: number;
	/** Per-AI wandering target angles for smooth movement (fallback) */
	aiWanderAngles: number[];
	/** Per-AI wandering angle change timers (fallback) */
	aiWanderTimers: number[];
	/** AI Brain state machine states */
	aiStates: AIState[];
	/** AI Brain think timers in milliseconds */
	aiThinkTimers: number[];
	/** AI Brain personalities */
	aiPersonalities: AIPersonality[];
	/** AI boost states */
	aiBoosting: boolean[];
	/** AI crawl states (for LEVIATHAN sharp turns) */
	aiCrawling: boolean[];
	/** Stats tracking */
	stats: {
		/** Total food eaten */
		foodEaten: number;
		/** Time alive in seconds */
		timeAlive: number;
		/** Maximum length reached */
		maxLength: number;
		/** Kills */
		kills: number;
	};
	/** Kill counts by type (for earned points calculation) */
	killCounts: KillCounts;
	/** Accumulated sprint time in seconds (for sprint multiplier system) */
	sprintTime: number;
	/** Whether slow crawl mode is active */
	isSlowCrawl: boolean;
	/** Whether mobile controls are enabled */
	mobileControlsEnabled: boolean;
	/** Mobile joystick angle in radians */
	mobileJoystickAngle: number;
	/** Mobile joystick force (0-1) */
	mobileJoystickForce: number;
	/** Timer for feast merge checks */
	feastMergeTimer: number;
	/** Current game time in seconds (for feast fresh drop tracking) */
	gameTime: number;
	/** Nightmare mode - all AI use legendary brain and visuals */
	nightmareMode: boolean;
	/** Timer for powerup spawning */
	powerupSpawnTimer: number;
	/** Timer for powerup decay check */
	powerupDecayTimer: number;
	/** Spawn times for each powerup (for decay calculation) */
	powerupSpawnTimes: Map<string, number>;
	/** World bounds (optional - for test mode with custom bounds) */
	worldBounds?: import('@wooorms/shared').WorldBounds;
	/** Test mode configuration (optional - only set in test mode) */
	testModeConfig?: TestModeConfig;
	/** Whether the game is paused (offline mode only) */
	paused: boolean;
	/** Shrinking zone state for battle royale mode */
	shrinkingState?: {
		/** Whether shrinking is enabled */
		enabled: boolean;
		/** Current phase */
		phase: ShrinkingPhase;
		/** Time in current phase */
		phaseTimer: number;
		/** Current shrink index (0 = first shrink) */
		shrinkIndex: number;
		/** Current safe zone bounds (centered on origin) */
		currentBounds: { x: number; y: number; width: number; height: number };
		/** Target bounds for next shrink */
		targetBounds: { x: number; y: number; width: number; height: number };
		/** Warning countdown (seconds) */
		warningTimer: number;
	};
}

/**
 * Online game state
 */
interface OnlineGameState {
	/** Network manager */
	networkManager: NetworkManager;
	/** State synchronization */
	stateSync: StateSync;
	/** Input manager */
	inputManager: InputManager;
	/** Food renderer */
	foodRenderer: FoodRenderer;
	/** Powerup renderer */
	powerupRenderer: PowerupRenderer;
	/** Fog of war system */
	fogOfWar: FogOfWar;
	/** Particle system for visual effects */
	particleSystem: ParticleSystem;
	/** Text popup system for kill effects */
	textPopupSystem: TextPopupSystem;
	/** Trail system for movement trails */
	trailSystem: TrailSystem;
	/** Player name */
	playerName: string;
	/** Player color */
	playerColor: number;
	/** Player skin */
	playerSkin?: SkinData;
	/** Player accessory */
	playerAccessory?: AccessoryType;
	/** Combo tracking */
	combo: {
		/** Current combo count */
		count: number;
		/** Time since last kill (seconds) */
		timeSinceKill: number;
	};
	/** Session ID of player who killed us (for revenge intel) */
	lastKillerId: string | null;
	/** Kill counts by type (for earned points calculation) */
	killCounts: KillCounts;
	/** Debug: Last time camera debug was logged */
	_lastCameraDebug?: number;
}

/**
 * PixiJS-based UI components
 */
interface PixiUI {
	minimap: Minimap;
	leaderboard: Leaderboard;
	powerupBar: PowerupBar;
	scoreDisplay: ScoreDisplay;
	killScoreDisplay: KillScoreDisplay;
	fuelBar: FuelBar;
	controlHints: ControlHints;
	sprintGauge: SprintGauge;
	nearMissSystem: NearMissSystem;
	revengeIntelArrow: RevengeIntelArrow;
	mobileControls: MobileControls;
	versionDisplay: VersionDisplay;
}

/**
 * Main application state
 */
interface AppState {
	game: Game;
	mode: GameMode;
	connectionUI: ConnectionUI;
	leaderboardUI: LeaderboardUI;
	deathUI: DeathUI;
	escMenu: EscMenu;
	pixiUI: PixiUI;
	offlineState: OfflineGameState | null;
	onlineState: OnlineGameState | null;
	/** Whether player is spectating after leaving (5 second fog view) */
	spectatingAfterLeave: boolean;
	/** Timer for spectate mode */
	spectateTimer: number;
}

/**
 * Main entry point for the WormCosmos client
 */
async function main(): Promise<void> {

	// Initialize sound system singleton (sounds will load on first user interaction)
	// This avoids the "AudioContext was prevented from starting automatically" warning
	SoundManager.getInstance();

	// Create and initialize the game
	const game = new Game();
	await game.init();

	// Create DOM-based UI components
	const connectionUI = createConnectionUI();
	const leaderboardUI = new LeaderboardUI();
	const deathUI = createDeathUI();
	const escMenu = createEscMenu();

	// Create PixiJS-based UI components
	const pixiUI = createPixiUI(game);

	// Initialize app state
	const appState: AppState = {
		game,
		mode: GameMode.MENU,
		connectionUI,
		leaderboardUI,
		deathUI,
		escMenu,
		pixiUI,
		offlineState: null,
		onlineState: null,
		spectatingAfterLeave: false,
		spectateTimer: 0,
	};

	// Setup UI event handlers
	setupUIHandlers(appState);

	// Setup game loop
	setupGameLoop(appState);

	// Add base UI elements
	setupBaseUI(game);

	// Show connection menu
	connectionUI.show();
}

/**
 * Create connection UI with handlers
 */
function createConnectionUI(): ConnectionUI {
	return new ConnectionUI({
		onConnect: () => { }, // Will be set in setupUIHandlers
		onOfflinePlay: () => { }, // Will be set in setupUIHandlers
	});
}

/**
 * Create death UI with handlers
 */
function createDeathUI(): DeathUI {
	return new DeathUI({
		onRespawn: () => { }, // Will be set in setupUIHandlers
		onQuit: () => { }, // Will be set in setupUIHandlers
	});
}

/**
 * Create ESC menu with handlers
 */
function createEscMenu(): EscMenu {
	return new EscMenu({
		onResume: () => { }, // Will be set in setupUIHandlers
		onLeave: () => { }, // Will be set in setupUIHandlers
	});
}

/**
 * Create PixiJS-based UI components
 */
function createPixiUI(game: Game): PixiUI {
	const uiContainer = game.uiContainer;

	// Create minimap
	const minimap = new Minimap({
		sizePercent: MINIMAP_SIZE_PERCENT,
		worldRadius: OFFLINE_WORLD_RADIUS,
	});
	uiContainer.addChild(minimap.container);

	// Create PixiJS leaderboard (positioned below minimap)
	const leaderboard = new Leaderboard({
		maxEntries: LEADERBOARD_MAX_ENTRIES,
		panelWidth: LEADERBOARD_PANEL_WIDTH,
	});
	uiContainer.addChild(leaderboard.container);

	// Create powerup bar
	const powerupBar = new PowerupBar();
	uiContainer.addChild(powerupBar.container);

	// Create score display
	const scoreDisplay = new ScoreDisplay();
	uiContainer.addChild(scoreDisplay.container);

	// Create kill score display (shows kills by type, positioned below score)
	const killScoreDisplay = new KillScoreDisplay();
	uiContainer.addChild(killScoreDisplay.container);

	// Create fuel bar
	const fuelBar = new FuelBar();
	uiContainer.addChild(fuelBar.container);

	// Create control hints (keyboard shortcuts)
	const controlHints = new ControlHints();
	uiContainer.addChild(controlHints.container);

	// Create sprint gauge (shows sprint multiplier progress)
	const sprintGauge = new SprintGauge();
	uiContainer.addChild(sprintGauge.container);

	// Create near miss system (screen flash effect for close calls)
	// Add at beginning of uiContainer so it appears behind other UI elements
	const nearMissSystem = new NearMissSystem();
	uiContainer.addChildAt(nearMissSystem.container, 0);

	// Create revenge intel arrow (points toward killer after respawn)
	const revengeIntelArrow = new RevengeIntelArrow();
	uiContainer.addChild(revengeIntelArrow.container);

	// Create mobile controls (joystick + action buttons)
	const mobileControls = new MobileControls();
	uiContainer.addChild(mobileControls.container);

	// Create version display (bottom-right corner)
	const versionDisplay = new VersionDisplay();
	uiContainer.addChild(versionDisplay.container);
	versionDisplay.show();

	// Initialize positions
	const width = game.screenWidth;
	const height = game.screenHeight;
	minimap.onResize(width, height);
	leaderboard.onResize(width, height);
	powerupBar.onResize(width, height);
	scoreDisplay.onResize(width, height);
	fuelBar.onResize(width, height);
	controlHints.onResize(width, height);
	sprintGauge.onResize(width, height);
	nearMissSystem.setScreenSize(width, height);
	revengeIntelArrow.setScreenSize(width, height);
	mobileControls.onResize(width, height);
	versionDisplay.onResize(width, height);

	// Position leaderboard below minimap
	const minimapBounds = minimap.getBounds();
	leaderboard.positionBelowMinimap(
		minimap.getBottomY(),
		minimapBounds.x + minimapBounds.width / 2
	);

	// Setup resize handler
	window.addEventListener('resize', () => {
		const w = game.screenWidth;
		const h = game.screenHeight;

		minimap.onResize(w, h);
		leaderboard.onResize(w, h);
		powerupBar.onResize(w, h);
		scoreDisplay.onResize(w, h);
		fuelBar.onResize(w, h);
		controlHints.onResize(w, h);
		sprintGauge.onResize(w, h);
		nearMissSystem.setScreenSize(w, h);
		revengeIntelArrow.setScreenSize(w, h);
		mobileControls.onResize(w, h);
		versionDisplay.onResize(w, h);

		// Reposition leaderboard below minimap
		const bounds = minimap.getBounds();
		leaderboard.positionBelowMinimap(
			minimap.getBottomY(),
			bounds.x + bounds.width / 2
		);
	});

	return {
		minimap,
		leaderboard,
		powerupBar,
		scoreDisplay,
		killScoreDisplay,
		fuelBar,
		controlHints,
		sprintGauge,
		nearMissSystem,
		revengeIntelArrow,
		mobileControls,
		versionDisplay,
	};
}

/**
 * Setup UI event handlers
 */
function setupUIHandlers(appState: AppState): void {
	const { connectionUI, deathUI, escMenu } = appState;

	// Replace connection UI with properly bound handlers
	const newConnectionUI = new ConnectionUI({
		onConnect: async (name: string, color: number, skin?: SkinData, accessory?: AccessoryType) => {
			await startOnlineGame(appState, name, color, skin, accessory);
		},
		onOfflinePlay: (skin?: SkinData, accessory?: AccessoryType, mode?: OfflineGameMode) => {
			startOfflineGame(appState, skin, accessory, mode);
		},
		onTestModePlay: (skin?: SkinData, accessory?: AccessoryType, config?: TestModeConfig) => {
			startTestModeGame(appState, skin, accessory, config);
		},
	});

	// Clean up old UI and replace
	connectionUI.destroy();
	appState.connectionUI = newConnectionUI;
	newConnectionUI.show();

	// Replace death UI with properly bound handlers
	const newDeathUI = new DeathUI({
		onRespawn: () => {
			handleRespawn(appState);
		},
		onQuit: () => {
			handleQuit(appState);
		},
	});

	deathUI.destroy();
	appState.deathUI = newDeathUI;

	// Replace ESC menu with properly bound handlers
	const newEscMenu = new EscMenu({
		onResume: () => {
			newEscMenu.hide();
			// Unpause offline game when resuming
			if (appState.mode === GameMode.OFFLINE && appState.offlineState) {
				appState.offlineState.paused = false;
			}
		},
		onLeave: () => {
			handleLeaveGame(appState);
		},
	});

	escMenu.destroy();
	appState.escMenu = newEscMenu;

	// Global ESC/P key listener for in-game menu and pause (offline mode)
	document.addEventListener('keydown', (e: KeyboardEvent) => {
		// ESC or P key to toggle pause menu
		if (e.key === 'Escape' || e.key === 'p' || e.key === 'P') {
			// Only show ESC menu if we're in game and not dead/spectating
			const isPlaying = appState.mode === GameMode.ONLINE || appState.mode === GameMode.OFFLINE;
			const isDead = appState.offlineState?.playerDead || false;
			const isSpectating = appState.spectatingAfterLeave;

			if (isPlaying && !isDead && !isSpectating && !appState.deathUI.isVisible()) {
				e.preventDefault();
				appState.escMenu.toggle();

				// In offline mode, also toggle the paused state
				if (appState.mode === GameMode.OFFLINE && appState.offlineState) {
					appState.offlineState.paused = appState.escMenu.isVisible();
				}
			}
		}

		// Toggle Performance Monitor
		if (e.key === 'F3') {
			e.preventDefault();
			PerformanceMonitor.getInstance().toggle();
		}
	});
}

/**
 * Start online multiplayer game
 */
async function startOnlineGame(appState: AppState, name: string, color: number, skin?: SkinData, accessory?: AccessoryType): Promise<void> {
	const { game, connectionUI, leaderboardUI, pixiUI } = appState;

	// Clean up any existing game state
	cleanupCurrentGame(appState);

	connectionUI.updateConnectionState(ConnectionState.CONNECTING);

	try {
		// Create network manager
		const networkManager = new NetworkManager();

		// Listen for connection state changes
		networkManager.on('stateChange', (state) => {
			connectionUI.updateConnectionState(state);
		});

		// Create food renderer
		const foodRenderer = new FoodRenderer({
			enablePulse: true,
			glowIntensity: WORM_GLOW_INTENSITY_DEFAULT,
		});

		// Create powerup renderer
		const powerupRenderer = new PowerupRenderer({
			enablePulse: true,
			glowIntensity: WORM_GLOW_INTENSITY_ONLINE,
		});

		// Create fog of war system
		const fogOfWar = new FogOfWar();
		fogOfWar.updatePlayerSize(DEFAULT_SEGMENT_COUNT);

		// Create particle system for visual effects
		const particleSystem = new ParticleSystem();

		// Create text popup system for kill effects
		const textPopupSystem = new TextPopupSystem();

		// Create trail system for movement trails and boost effects
		const trailSystem = new TrailSystem();

		// Add to food layer (powerups render on top of food)
		const foodLayer = game.getLayer(GameLayer.FOOD);
		foodLayer.addChild(foodRenderer.container);
		foodLayer.addChild(powerupRenderer.container);

		// Create state sync with particle system for death effects
		const wormsLayer = game.getLayer(GameLayer.WORMS);
		// Add trail system first so trails render behind worms
		wormsLayer.addChildAt(trailSystem.container, 0);
		wormsLayer.addChild(particleSystem.container);
		wormsLayer.addChild(textPopupSystem.container);
		const stateSync = new StateSync(
			wormsLayer,
			foodRenderer,
			powerupRenderer,
			undefined, // Use default interpolation buffer
			particleSystem
		);

		// Create input manager
		const inputManager = new InputManager(game.camera, {
			canvas: game.app.canvas as HTMLCanvasElement,
			sendRate: INPUT_SEND_RATE,
		});

		// Connect to server with skin data
		const joinOptions = { name, color, ...(skin && { skin }) };
		const room = await networkManager.connect(joinOptions);

		// Wait for the first state change to ensure schema is fully received
		// This is required because Colyseus sends schema after WebSocket connects
		await new Promise<void>((resolve) => {
			room.onStateChange.once(() => {
				resolve();
			});
		});

		// Debug: Log room state structure for troubleshooting
		console.log('[Main] room.state after onStateChange:', room.state);
		console.log('[Main] room.state keys:', Object.keys(room.state as object));
		console.log('[Main] room.state own property names:', Object.getOwnPropertyNames(room.state as object));
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const roomState = room.state as any;
		console.log('[Main] room.state.worms:', roomState?.worms);
		console.log('[Main] room.state.$changes:', roomState?.$changes);
		console.log('[Main] room.state constructor:', roomState?.constructor?.name);
		// Try to log all enumerable and non-enumerable properties
		const descriptors = Object.getOwnPropertyDescriptors(room.state as object);
		console.log('[Main] room.state property descriptors:', descriptors);
		// Check prototype chain
		let proto = Object.getPrototypeOf(room.state);
		let protoLevel = 0;
		while (proto && protoLevel < 3) {
			console.log(`[Main] room.state proto level ${protoLevel}:`, Object.keys(proto), Object.getOwnPropertyNames(proto));
			proto = Object.getPrototypeOf(proto);
			protoLevel++;
		}

		// If worms is undefined, try to wait for more state updates
		if (!roomState?.worms) {
			console.log('[Main] worms is undefined, waiting for additional state updates...');
			await new Promise<void>((resolve) => {
				let attempts = 0;
				const maxAttempts = 10;
				const checkInterval = setInterval(() => {
					attempts++;
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					const state = room.state as any;
					console.log(`[Main] Attempt ${attempts}: checking worms =`, state?.worms);
					if (state?.worms || attempts >= maxAttempts) {
						clearInterval(checkInterval);
						resolve();
					}
				}, 100);
			});
			console.log('[Main] After waiting, room.state.worms:', roomState?.worms);
		}

		// Set local player's accessory before starting sync
		if (accessory && accessory !== AccessoryType.NONE) {
			stateSync.setLocalPlayerAccessory(accessory);
		}

		// Start state synchronization
		stateSync.startSync(room, room.sessionId);

		// Start input sending
		inputManager.start(networkManager);

		// Setup leaderboard updates (both DOM and PixiJS versions)
		// Ensure local player is always shown, even if not in top 10
		stateSync.on('leaderboardUpdate', (entries) => {
			const localPlayerId = room.sessionId;
			const localPlayerInLeaderboard = entries.some(e => e.id === localPlayerId);

			// If local player is not in top 10, append them at the end
			if (!localPlayerInLeaderboard) {
				const localWorm = stateSync.getLocalWorm();
				if (localWorm && localWorm.alive) {
					const entriesWithPlayer = [...entries];
					// Find player's rank by counting how many have higher scores
					const playerRank = entries.filter(e => e.score > localWorm.score).length + entries.length + 1;
					entriesWithPlayer.push({
						id: localPlayerId,
						name: localWorm.name,
						score: localWorm.score,
						rank: playerRank,
					});
					leaderboardUI.update(entriesWithPlayer);
					pixiUI.leaderboard.update(entriesWithPlayer);
					return;
				}
			}

			leaderboardUI.update(entries);
			pixiUI.leaderboard.update(entries);
		});

		// Setup death handling
		stateSync.on('localPlayerDied', (killedBy, finalScore) => {
			// Store killer's session ID for revenge intel
			if (appState.onlineState && killedBy) {
				appState.onlineState.lastKillerId = killedBy;
			}
			handlePlayerDeath(appState, killedBy, finalScore);
		});

		// Combo tracking state (will be stored in onlineState)
		const comboState = { count: 0, timeSinceKill: COMBO_TIME_WINDOW + 1 };

		// Kill tracking state (will be stored in onlineState)
		const killCountsState = createEmptyKillCounts();

		// Setup kill effect handling - when local player kills someone
		stateSync.on('gotKill', (_victimId, victimName, x, y, victimColor, victimType) => {
			// Show kill text popup at victim location
			textPopupSystem.spawnKillText(x, y, victimName);
			// Spawn kill particle effect
			particleSystem.spawnKillEffect(x, y, victimColor);

			// Track kill by type
			killCountsState[victimType]++;
			pixiUI.killScoreDisplay.addKill(victimType);

			// Update combo tracking
			if (comboState.timeSinceKill <= COMBO_TIME_WINDOW) {
				// Within combo window - increment combo
				comboState.count++;
			} else {
				// Start new combo
				comboState.count = 1;
			}
			comboState.timeSinceKill = 0;

			// Show combo popup if threshold met
			if (comboState.count >= COMBO_MIN_KILLS) {
				textPopupSystem.spawnComboText(x, y - COMBO_POPUP_Y_OFFSET, comboState.count);
				// Play combo sound with escalating pitch based on combo count
				SoundManager.getInstance().playWithPitch(SoundType.COMBO, comboState.count);
			}
		});

		// Store online state
		appState.onlineState = {
			networkManager,
			stateSync,
			inputManager,
			foodRenderer,
			powerupRenderer,
			fogOfWar,
			particleSystem,
			textPopupSystem,
			trailSystem,
			playerName: name,
			playerColor: color,
			...(skin && { playerSkin: skin }),
			...(accessory && accessory !== AccessoryType.NONE && { playerAccessory: accessory }),
			combo: comboState,
			lastKillerId: null,
			killCounts: killCountsState,
		};

		// Set mode and show/hide UI
		appState.mode = GameMode.ONLINE;
		connectionUI.hide();
		leaderboardUI.setLocalPlayerId(room.sessionId);
		leaderboardUI.show();

		// Connect near miss system to text popup system for "CLOSE!" text
		pixiUI.nearMissSystem.setTextPopupSystem(textPopupSystem);

		// Show PixiJS UI components
		pixiUI.minimap.show();
		// Note: Using DOM LeaderboardUI (with medals) instead of PixiJS leaderboard for online mode
		// pixiUI.leaderboard.setLocalPlayerId(room.sessionId);
		// pixiUI.leaderboard.show();
		pixiUI.powerupBar.show();
		// Hide fuel bar - sprint is completely free now
		pixiUI.fuelBar.hide();
		pixiUI.scoreDisplay.show();
		pixiUI.scoreDisplay.setScore(0, false);
		pixiUI.killScoreDisplay.show();
		pixiUI.killScoreDisplay.reset();
		// Position killScoreDisplay below scoreDisplay
		pixiUI.killScoreDisplay.setPosition(15, 90);

		// Start camera at spawn position and play zoom-in animation
		game.camera.snapTo(0, 0);
		game.camera.playStartAnimation();
	} catch (error) {
		console.error('Failed to connect:', error);
		connectionUI.updateConnectionState(ConnectionState.ERROR);
		connectionUI.setStatus('Failed to connect. Server may be offline.', '#ff6b6b');
	}
}

/**
 * Start offline single-player game
 */
function startOfflineGame(appState: AppState, skin?: SkinData, accessory?: AccessoryType, mode?: OfflineGameMode): void {
	const { game, connectionUI, leaderboardUI, pixiUI } = appState;

	// Clean up any existing game state
	cleanupCurrentGame(appState);

	// Setup offline game state with skin, accessory, and mode
	const offlineState = setupOfflineGame(game, skin, accessory, mode);

	// Setup offline input handlers
	setupOfflineInputHandlers(game, offlineState, appState.pixiUI);

	// Store state
	appState.offlineState = offlineState;
	appState.mode = GameMode.OFFLINE;

	// Hide connection UI, hide DOM leaderboard (not needed offline)
	connectionUI.hide();
	leaderboardUI.hide();

	// Show PixiJS UI components for offline mode
	pixiUI.minimap.show();
	pixiUI.leaderboard.show(); // Show leaderboard with AI worms
	pixiUI.powerupBar.show();
	pixiUI.scoreDisplay.show();
	// Score = segment count (1:1 ratio like online mode)
	pixiUI.scoreDisplay.setScore(offlineState.score, false);
	pixiUI.killScoreDisplay.show();
	pixiUI.killScoreDisplay.reset();
	// Position killScoreDisplay below scoreDisplay
	pixiUI.killScoreDisplay.setPosition(15, 90);
}

/**
 * Start a test mode game with custom configuration
 */
function startTestModeGame(appState: AppState, skin?: SkinData, accessory?: AccessoryType, config?: TestModeConfig): void {
	const { game, connectionUI, leaderboardUI, pixiUI } = appState;

	// Clean up any existing game state
	cleanupCurrentGame(appState);

	// Setup test mode game state with custom config
	const offlineState = setupTestModeGame(game, skin, accessory, config);

	// Setup offline input handlers (same as offline mode)
	setupOfflineInputHandlers(game, offlineState, appState.pixiUI);

	// Store state
	appState.offlineState = offlineState;
	appState.mode = GameMode.OFFLINE;

	// Hide connection UI, hide DOM leaderboard
	connectionUI.hide();
	leaderboardUI.hide();

	// Show PixiJS UI components for test mode
	pixiUI.minimap.show();
	pixiUI.leaderboard.show();
	pixiUI.powerupBar.show();
	pixiUI.scoreDisplay.show();
	pixiUI.scoreDisplay.setScore(offlineState.score, false);
	pixiUI.killScoreDisplay.show();
	pixiUI.killScoreDisplay.reset();
	pixiUI.killScoreDisplay.setPosition(15, 90);

	// Update minimap for rectangular maps
	if (config?.mapShape === MapShape.RECTANGULAR && offlineState.worldBounds) {
		const dimensions = offlineState.worldBounds.getDimensions();
		pixiUI.minimap.setMapShape(MapShape.RECTANGULAR, dimensions.width, dimensions.height);
	}
}

/**
 * Setup test mode game elements with custom configuration
 * Delegates to setupOfflineGame and adds test mode specific configuration
 */
function setupTestModeGame(game: Game, skin?: SkinData, accessory?: AccessoryType, config?: TestModeConfig): OfflineGameState {
	// Use default config if not provided
	const { createWorldBounds, getDefaultTestModeConfig } = require('@wooorms/shared');
	const testConfig = config ?? getDefaultTestModeConfig();

	// Create world bounds based on config using factory function
	const worldBounds = createWorldBounds(testConfig.mapShape, testConfig.mapSize);

	// Delegate to existing offline game setup (classic mode as base)
	const state = setupOfflineGame(game, skin, accessory, OfflineGameMode.CLASSIC);

	// Attach test mode specific configuration
	state.worldBounds = worldBounds;
	state.testModeConfig = testConfig;

	// Initialize shrinking zone state if enabled
	if (testConfig.shrinkingConfig?.enabled && testConfig.mapShape === MapShape.RECTANGULAR) {
		const dims = worldBounds.getDimensions();
		state.shrinkingState = {
			enabled: true,
			phase: ShrinkingPhase.WAITING,
			phaseTimer: 0,
			shrinkIndex: 0,
			currentBounds: { x: 0, y: 0, width: dims.width, height: dims.height },
			targetBounds: { x: 0, y: 0, width: dims.width, height: dims.height },
			warningTimer: 0,
		};
	}

	// Adjust powerup count to match preset's initialCount
	const ratePreset = testConfig.powerupRatePreset;
	if (ratePreset) {
		const currentCount = state.powerupRenderer.getPowerupCount();
		const targetCount = ratePreset.initialCount;

		if (targetCount > currentCount) {
			// ADD more powerups to reach target
			const additionalCount = targetCount - currentCount;
			for (let i = 0; i < additionalCount; i++) {
				const newPowerup = spawnRandomPowerup(state.nextPowerupId++);
				state.powerupRenderer.addPowerup(newPowerup);
				state.powerupSpawnTimes.set(newPowerup.id, 0);
			}
		} else if (targetCount < currentCount) {
			// REMOVE excess powerups to reach target
			const allPowerups = state.powerupRenderer.getAllPowerups();
			const excessCount = currentCount - targetCount;
			// Remove from the end of the list (most recently added)
			for (let i = 0; i < excessCount && i < allPowerups.length; i++) {
				const powerup = allPowerups[allPowerups.length - 1 - i];
				if (powerup) {
					state.powerupRenderer.removePowerup(powerup.id);
					state.powerupSpawnTimes.delete(powerup.id);
				}
			}
		}
	}

	// Apply visibility preset to fog of war
	const visPreset = testConfig.visibilityPreset;
	if (visPreset) {
		state.fogOfWar.setVisibilityPreset(visPreset.radiusMultiplier, visPreset.fogDisabled);
	}

	return state;
}

/**
 * Setup offline game elements (similar to original test game)
 */
function setupOfflineGame(game: Game, skin?: SkinData, accessory?: AccessoryType, mode?: OfflineGameMode): OfflineGameState {
	// Determine if nightmare mode (all AI are legendary)
	const nightmareMode = mode === OfflineGameMode.NIGHTMARE;
	// Convert SkinData to WormSkinData format for WormRenderer
	const wormSkin = skin ? {
		type: skin.type,
		id: skin.id,
		primaryColor: skin.primaryColor,
		...(skin.secondaryColor && { secondaryColor: skin.secondaryColor }),
		...(skin.pattern && { pattern: skin.pattern }),
		...(skin.colors && { colors: skin.colors }),
	} : undefined;

	// Score = segment count * SCORE_PER_SEGMENT (2 points per segment)
	const initialScore = OFFLINE_PLAYER_SEGMENT_COUNT * GROWTH.SCORE_PER_SEGMENT;
	// Calculate radius based on score (same as server)
	const initialRadius = calculateWormRadius(initialScore);

	// Create player worm with skin and score-based radius
	const playerWorm = new WormRenderer({
		color: WORM_COLORS[0],
		segmentCount: OFFLINE_PLAYER_SEGMENT_COUNT,
		radius: initialRadius,
		segmentSpacing: OFFLINE_PLAYER_SEGMENT_SPACING,
		...(wormSkin && { skin: wormSkin }),
	});

	// Set accessory if provided
	if (accessory && accessory !== AccessoryType.NONE) {
		playerWorm.setAccessory(accessory);
	}

	// Mark as local player for golden aura effect
	playerWorm.setIsLocalPlayer(true);

	// Add player worm to worms layer
	const wormsLayer = game.getLayer(GameLayer.WORMS);
	wormsLayer.addChild(playerWorm.container);

	// Initialize player position at center
	playerWorm.updateHeadPosition(0, 0);

	// Snap camera to player and play zoom-in animation
	game.camera.snapTo(0, 0);
	game.camera.playStartAnimation();

	// Create some test AI worms with names from AI_CHARACTERS
	const otherWorms: WormRenderer[] = [];
	const aiNames: string[] = [];
	const aiSegmentCounts: number[] = [];
	const aiStates: AIState[] = [];
	const aiThinkTimers: number[] = [];
	const aiPersonalities: AIPersonality[] = [];
	const aiBoosting: boolean[] = [];
	const aiCrawling: boolean[] = [];

	// Shuffle AI_CHARACTERS to get random names without duplicates
	const shuffledCharacters = [...AI_CHARACTERS].sort(() => Math.random() - 0.5);

	// Track all spawn positions to prevent overlaps
	const spawnPositions: Array<{ x: number; y: number }> = [];
	// Player is at center - add as occupied position
	spawnPositions.push({ x: 0, y: 0 });

	// Minimum distance between any two worms (prevents spawn kills)
	const MIN_SPAWN_SEPARATION = 400;

	for (let i = 0; i < OFFLINE_AI_WORM_COUNT; i++) {
		const colorIndex = (i + 1) % WORM_COLORS.length;
		const wormColor = WORM_COLORS[colorIndex] ?? 0x4ecdc4;
		const worm = new WormRenderer({
			color: wormColor,
			segmentCount: OFFLINE_AI_MIN_SEGMENTS + Math.floor(Math.random() * OFFLINE_AI_RANDOM_SEGMENTS),
			radius: OFFLINE_AI_MIN_RADIUS + Math.random() * OFFLINE_AI_RANDOM_RADIUS,
			segmentSpacing: OFFLINE_AI_SEGMENT_SPACING,
		});

		// Assign a unique name from shuffled characters (cycle if more worms than names)
		const character = shuffledCharacters[i % shuffledCharacters.length];
		aiNames.push(character?.name ?? `Bot ${i + 1}`);

		// Initialize AI brain state for this worm
		aiStates.push(AIState.HUNT_FOOD);
		aiThinkTimers.push(Math.random() * AI_BRAIN_THINK_INTERVAL_MS); // Stagger think times
		// In Nightmare mode, all AI use Legendary personality
		const personality = nightmareMode
			? AI_PERSONALITIES.Legendary
			: (character?.personality ?? AI_PERSONALITIES.Hunter);
		aiPersonalities.push(personality);
		aiBoosting.push(false);
		aiCrawling.push(false);

		// In Nightmare mode, set legendary visual style
		if (nightmareMode) {
			worm.setLegendary(true);
		}

		// Find a safe spawn position (not overlapping with player or other AIs)
		let x = 0;
		let y = 0;
		let attempts = 0;
		const maxAttempts = 50;

		while (attempts < maxAttempts) {
			// Random position in a ring around center
			const angle = Math.random() * Math.PI * 2;
			const distance = OFFLINE_AI_MIN_SPAWN_DISTANCE + Math.random() * OFFLINE_AI_RANDOM_SPAWN_DISTANCE;
			x = Math.cos(angle) * distance;
			y = Math.sin(angle) * distance;

			// Check distance from all existing spawn positions
			let tooClose = false;
			for (const pos of spawnPositions) {
				const dx = x - pos.x;
				const dy = y - pos.y;
				const dist = Math.sqrt(dx * dx + dy * dy);
				if (dist < MIN_SPAWN_SEPARATION) {
					tooClose = true;
					break;
				}
			}

			if (!tooClose) {
				break; // Found a safe position
			}
			attempts++;
		}

		// Record this spawn position
		spawnPositions.push({ x, y });
		worm.updateHeadPosition(x, y);

		// Assign random accessory to AI worms (70% chance to have one)
		if (Math.random() < 0.7) {
			const accessories = [
				AccessoryType.HEADPHONES,
				AccessoryType.PARTY_HAT,
				AccessoryType.CROWN,
				AccessoryType.SUNGLASSES,
				AccessoryType.BOW_TIE,
				AccessoryType.HALO,
				AccessoryType.DEVIL_HORNS,
				AccessoryType.BANDANA,
				AccessoryType.WIZARD_HAT,
				AccessoryType.ANTENNA,
				AccessoryType.TOP_HAT,
				AccessoryType.FLOWER_CROWN,
				AccessoryType.PIRATE_HAT,
				AccessoryType.VIKING_HELMET,
				AccessoryType.CAT_EARS,
			];
			const randomAccessory = accessories[Math.floor(Math.random() * accessories.length)];
			if (randomAccessory) {
				worm.setAccessory(randomAccessory);
			}
		}

		wormsLayer.addChild(worm.container);
		otherWorms.push(worm);

		// Track AI segment count for collision/death drops
		aiSegmentCounts.push(worm.getSegmentCount());
	}

	// Create food renderer
	const foodRenderer = new FoodRenderer({
		enablePulse: true,
		glowIntensity: WORM_GLOW_INTENSITY_DEFAULT,
	});

	// Add to food layer
	const foodLayer = game.getLayer(GameLayer.FOOD);
	foodLayer.addChild(foodRenderer.container);

	// Spawn test food - density-based count that scales with world size
	const offlineFoodCount = calculateFoodCount(OFFLINE_WORLD_RADIUS);
	foodRenderer.spawnTestFood(0, 0, OFFLINE_WORLD_RADIUS * OFFLINE_FOOD_SPAWN_RADIUS_PERCENT, offlineFoodCount);

	// Create powerup renderer
	const powerupRenderer = new PowerupRenderer({
		glowIntensity: 0.6,
		enablePulse: true,
		showLabels: true,
	});
	foodLayer.addChild(powerupRenderer.container);

	// Spawn initial powerups based on density config
	const targetPowerupCount = calculatePowerupCount(OFFLINE_WORLD_RADIUS);
	const initialPowerupSpawnTimes = new Map<string, number>();
	let nextPowerupId = 0;
	for (let i = 0; i < targetPowerupCount; i++) {
		const powerup = spawnRandomPowerup(nextPowerupId++);
		powerupRenderer.addPowerup(powerup);
		// Record spawn time (initial powerups spawn at time 0)
		initialPowerupSpawnTimes.set(powerup.id, 0);
	}

	// Create fog of war system
	const fogOfWar = new FogOfWar();
	const initialSegmentCount = OFFLINE_PLAYER_SEGMENT_COUNT;
	fogOfWar.updatePlayerSize(initialSegmentCount);
	fogOfWar.updatePlayerPosition(0, 0);

	// Create death effect system
	const deathEffects = new DeathEffectSystem();
	wormsLayer.addChild(deathEffects.getContainer());

	// Create particle system for visual effects
	const particleSystem = new ParticleSystem();
	wormsLayer.addChild(particleSystem.container);

	// Create eating radius indicator graphics (renders behind the player worm)
	const eatingRadiusGraphics = new Graphics();
	eatingRadiusGraphics.label = 'eating-radius';
	wormsLayer.addChildAt(eatingRadiusGraphics, 0); // Add at bottom of layer

	// Get initial angle from worm renderer (defaults to 0)
	const initialAngle = playerWorm.getHeadRotation();

	return {
		playerWorm,
		otherWorms,
		foodRenderer,
		powerupRenderer,
		fogOfWar,
		deathEffects,
		particleSystem,
		eatingRadiusGraphics,
		mouseWorldPos: { x: 0, y: 0 },
		mouseScreenPos: { x: window.innerWidth / 2, y: window.innerHeight / 2 },
		speed: DEFAULT_WORM_SPEED,
		boosting: false,
		fuel: 100, // Start with full fuel
		boostBurnAccumulator: 0,
		score: initialScore, // Score = segment count (1:1 ratio)
		currentAngle: initialAngle,
		segmentCount: initialSegmentCount,
		playerDead: false,
		lastKillerIndex: null,
		aiNames,
		aiSegmentCounts,
		activePowerups: new Map(),
		nextPowerupId,
		paused6x: null,
		activeOverride: null,
		speedStacks: 0,
		magnetActive: false,
		agilityActive: false,
		visionActive: false,
		slowModeTimer: 0,
		slowModeScoreAccumulator: 0,
		growthAccumulator: 0,
		dyingWorms: [],
		aiRespawnTimer: 0,
		aiWanderAngles: aiNames.map(() => Math.random() * Math.PI * 2),
		aiWanderTimers: aiNames.map(() => Math.random() * 3),
		aiStates,
		aiThinkTimers,
		aiPersonalities,
		aiBoosting,
		aiCrawling,
		stats: {
			foodEaten: 0,
			timeAlive: 0,
			maxLength: initialSegmentCount,
			kills: 0,
		},
		killCounts: createEmptyKillCounts(),
		sprintTime: 0,
		isSlowCrawl: false,
		mobileControlsEnabled: MobileControls.shouldShow(),
		mobileJoystickAngle: 0,
		mobileJoystickForce: 0,
		feastMergeTimer: 0,
		gameTime: 0,
		nightmareMode,
		powerupSpawnTimer: 0,
		powerupDecayTimer: 0,
		powerupSpawnTimes: initialPowerupSpawnTimes,
		paused: false,
	};
}

/**
 * Spawn a random powerup at a random position in the world
 */
function spawnRandomPowerup(id: number): PowerupItem {
	// Get powerup type based on spawn weights
	const powerupTypes = Object.values(PowerupType);
	const weights = powerupTypes.map((type) => POWERUP_CONFIGS[type].spawnWeight);
	const totalWeight = weights.reduce((sum, w) => sum + w, 0);

	let random = Math.random() * totalWeight;
	let selectedType = PowerupType.MULTIPLIER_2X;

	for (let i = 0; i < powerupTypes.length; i++) {
		random -= weights[i]!;
		if (random <= 0) {
			selectedType = powerupTypes[i]!;
			break;
		}
	}

	const config = POWERUP_CONFIGS[selectedType];

	// Random position within world bounds (not too close to edge)
	const maxRadius = OFFLINE_WORLD_RADIUS * OFFLINE_POWERUP_SPAWN_RADIUS_PERCENT;
	const angle = Math.random() * Math.PI * 2;
	const distance = Math.random() * maxRadius;

	return {
		id: `powerup_${id}`,
		x: Math.cos(angle) * distance,
		y: Math.sin(angle) * distance,
		radius: config.radius,
		color: config.color,
		powerupType: selectedType,
	};
}

/**
 * Spawn a single AI worm and add it to the game state
 */
function spawnAIWorm(
	state: OfflineGameState,
	wormsLayer: Container,
	existingPositions: Array<{ x: number; y: number }>
): void {
	// Pick a random color
	const colorIndex = Math.floor(Math.random() * WORM_COLORS.length);
	const wormColor = WORM_COLORS[colorIndex] ?? 0x4ecdc4;

	// Create the worm
	const worm = new WormRenderer({
		color: wormColor,
		segmentCount: OFFLINE_AI_MIN_SEGMENTS + Math.floor(Math.random() * OFFLINE_AI_RANDOM_SEGMENTS),
		radius: OFFLINE_AI_MIN_RADIUS + Math.random() * OFFLINE_AI_RANDOM_RADIUS,
		segmentSpacing: OFFLINE_AI_SEGMENT_SPACING,
	});

	// Pick a random name from AI_CHARACTERS that isn't already used
	const usedNames = new Set(state.aiNames);
	const availableCharacters = AI_CHARACTERS.filter(c => !usedNames.has(c.name));
	const character = availableCharacters.length > 0
		? availableCharacters[Math.floor(Math.random() * availableCharacters.length)]
		: AI_CHARACTERS[Math.floor(Math.random() * AI_CHARACTERS.length)];
	const aiName = character?.name ?? `Bot ${state.otherWorms.length + 1}`;

	// Find a safe spawn position (keep AI worms well spread out)
	const MIN_SPAWN_SEPARATION = 800;
	let x = 0;
	let y = 0;
	let attempts = 0;
	const maxAttempts = 50;

	while (attempts < maxAttempts) {
		const angle = Math.random() * Math.PI * 2;
		const distance = OFFLINE_AI_MIN_SPAWN_DISTANCE + Math.random() * OFFLINE_AI_RANDOM_SPAWN_DISTANCE;
		x = Math.cos(angle) * distance;
		y = Math.sin(angle) * distance;

		// Check distance from all existing positions
		let tooClose = false;
		for (const pos of existingPositions) {
			const dx = x - pos.x;
			const dy = y - pos.y;
			const dist = Math.sqrt(dx * dx + dy * dy);
			if (dist < MIN_SPAWN_SEPARATION) {
				tooClose = true;
				break;
			}
		}

		if (!tooClose) break;
		attempts++;
	}

	worm.updateHeadPosition(x, y);

	// Assign random accessory (70% chance)
	if (Math.random() < 0.7) {
		const accessories = [
			AccessoryType.HEADPHONES,
			AccessoryType.PARTY_HAT,
			AccessoryType.CROWN,
			AccessoryType.SUNGLASSES,
			AccessoryType.BOW_TIE,
			AccessoryType.HALO,
			AccessoryType.DEVIL_HORNS,
			AccessoryType.BANDANA,
			AccessoryType.WIZARD_HAT,
			AccessoryType.ANTENNA,
			AccessoryType.TOP_HAT,
			AccessoryType.FLOWER_CROWN,
			AccessoryType.PIRATE_HAT,
			AccessoryType.VIKING_HELMET,
			AccessoryType.CAT_EARS,
		];
		const randomAccessory = accessories[Math.floor(Math.random() * accessories.length)];
		if (randomAccessory) {
			worm.setAccessory(randomAccessory);
		}
	}

	// Add to game
	wormsLayer.addChild(worm.container);
	state.otherWorms.push(worm);
	state.aiNames.push(aiName);
	state.aiSegmentCounts.push(worm.getSegmentCount());
	state.aiWanderAngles.push(Math.random() * Math.PI * 2);
	state.aiWanderTimers.push(Math.random() * 3);

	// Initialize AI brain state for this worm
	state.aiStates.push(AIState.HUNT_FOOD);
	state.aiThinkTimers.push(Math.random() * AI_BRAIN_THINK_INTERVAL_MS); // Stagger think times
	// In Nightmare mode, all AI use Legendary personality
	const personality = state.nightmareMode
		? AI_PERSONALITIES.Legendary
		: (character?.personality ?? AI_PERSONALITIES.Hunter);
	state.aiPersonalities.push(personality);
	state.aiBoosting.push(false);
	state.aiCrawling.push(false);

	// In Nightmare mode, set legendary visual style
	if (state.nightmareMode) {
		worm.setLegendary(true);
	}
}

/**
 * Check and respawn AI worms if needed
 */
function checkAIRespawn(
	state: OfflineGameState,
	wormsLayer: Container,
	dt: number
): void {
	// Update timer
	state.aiRespawnTimer += dt;

	// Only check periodically
	if (state.aiRespawnTimer < OFFLINE_AI_RESPAWN_INTERVAL) return;
	state.aiRespawnTimer = 0;

	// Check if we need to spawn more AI worms
	const currentCount = state.otherWorms.length;
	if (currentCount >= OFFLINE_AI_MIN_COUNT) return;

	// Calculate how many to spawn (up to target count, but only 1 at a time for gradual respawn)
	const toSpawn = Math.min(
		OFFLINE_AI_TARGET_COUNT - currentCount,
		1 // Only spawn 1 per cycle for gradual, less crowded respawns
	);

	// Collect existing worm positions for safe spawning
	const existingPositions: Array<{ x: number; y: number }> = [];
	const playerHead = state.playerWorm.getHeadPosition();
	existingPositions.push({ x: playerHead.x, y: playerHead.y });

	for (const worm of state.otherWorms) {
		const pos = worm.getHeadPosition();
		existingPositions.push({ x: pos.x, y: pos.y });
	}

	// Spawn the AI worms
	for (let i = 0; i < toSpawn; i++) {
		spawnAIWorm(state, wormsLayer, existingPositions);
		// Update positions list with newly spawned worm
		const newWorm = state.otherWorms[state.otherWorms.length - 1];
		if (newWorm) {
			const pos = newWorm.getHeadPosition();
			existingPositions.push({ x: pos.x, y: pos.y });
		}
	}
}

/**
 * Check if player collected any powerups
 */
function checkPowerupCollection(
	state: OfflineGameState,
	x: number,
	y: number,
	radius: number,
	pixiUI: PixiUI
): void {
	const { powerupRenderer } = state;
	const collectionRadius = radius + 20; // Slightly larger than worm radius

	// Get nearby powerups using the public method
	const nearbyPowerups = powerupRenderer.getPowerupsNear(x, y, collectionRadius);

	// Collect all nearby powerups
	for (const powerup of nearbyPowerups) {
		// Activate the powerup effect
		activatePowerup(state, powerup.powerupType, pixiUI);

		// Remove from renderer and spawn time tracking
		powerupRenderer.removePowerup(powerup.id);
		state.powerupSpawnTimes.delete(powerup.id);
	}
}

/**
 * Draw the eating radius indicator at the front of the player's head
 * Shows a subtle circle for base eating and a larger one when magnet is active
 * The eating zone is positioned forward from the head center based on rotation
 */
function drawEatingRadius(
	state: OfflineGameState,
	x: number,
	y: number,
	headRadius: number,
	headRotation: number
): void {
	const g = state.eatingRadiusGraphics;
	g.clear();

	// Calculate forward offset position (eating zone is at the front of the head)
	const forwardOffset = headRadius * EATING_FORWARD_OFFSET;
	const eatingX = x + Math.cos(headRotation) * forwardOffset;
	const eatingY = y + Math.sin(headRotation) * forwardOffset;

	// Base eating radius (head + BASE_FOOD_PICKUP_OFFSET)
	const baseEatingRadius = headRadius + BASE_FOOD_PICKUP_OFFSET;

	// Draw base eating radius - subtle dotted-like circle at the front
	g.circle(eatingX, eatingY, baseEatingRadius);
	g.stroke({ color: 0xffffff, alpha: 0.25, width: 2 });

	// Inner glow for base eating
	g.circle(eatingX, eatingY, baseEatingRadius);
	g.fill({ color: 0xffffff, alpha: 0.05 });

	// If magnet is active, draw the larger magnet radius (centered on head for wider pull)
	if (state.magnetActive) {
		const magnetRadius = MAGNET_RADIUS + (headRadius * MAGNET_HEAD_SCALE);

		// Outer magnet glow - centered on forward position
		g.circle(eatingX, eatingY, magnetRadius);
		g.fill({ color: 0xff00ff, alpha: 0.08 });

		// Magnet radius ring - purple/magenta color
		g.circle(eatingX, eatingY, magnetRadius);
		g.stroke({ color: 0xff00ff, alpha: 0.4, width: 3 });

		// Inner ring for magnet effect
		g.circle(eatingX, eatingY, magnetRadius * 0.85);
		g.stroke({ color: 0xff66ff, alpha: 0.2, width: 1 });
	}
}

/**
 * Powerup segment bonus - each powerup grants +5 segments
 */
const POWERUP_SEGMENT_BONUS = 5;

/**
 * Calculate the total powerup multiplier from active multiplier powerups
 *
 * Multiplier Tiers:
 * - Base Tier (2x, 4x): Stack multiplicatively with each other
 * - Final Slot (6x): Only one active
 * - Override (10x, 15x): Temporarily replaces Final Slot
 *
 * Total = 2x × 4x × (6x OR override)
 */
function calculatePowerupMultiplier(state: OfflineGameState): number {
	let multiplier = 1;

	// Base Tier - 2x and 4x stack multiplicatively
	if (state.activePowerups.has(PowerupType.MULTIPLIER_2X)) {
		multiplier *= 2;
	}
	if (state.activePowerups.has(PowerupType.MULTIPLIER_4X)) {
		multiplier *= 4;
	}

	// Final Slot OR Override (mutually exclusive)
	if (state.activeOverride === PowerupType.MULTIPLIER_15X) {
		multiplier *= 15;
	} else if (state.activeOverride === PowerupType.MULTIPLIER_10X) {
		multiplier *= 10;
	} else if (state.activePowerups.has(PowerupType.MULTIPLIER_6X)) {
		multiplier *= 6;
	}

	return multiplier;
}

/**
 * Activate a collected powerup
 * All powerups add +30 seconds and cap at 10 minutes (600 seconds)
 * All powerups also grant +5 segments when collected
 */
function activatePowerup(state: OfflineGameState, type: PowerupType, pixiUI: PixiUI): void {
	const config = POWERUP_CONFIGS[type];
	const ADD_TIME = 30; // +30 seconds per pickup
	const MAX_TIME = 600; // 10 minutes cap

	// Play powerup collection sound
	SoundManager.getInstance().play(SoundType.POWERUP);

	// Award score for collecting powerup (multiplied by powerup and sprint multipliers)
	const sprintTier = getSprintTier(state.sprintTime);
	const powerupMultiplier = calculatePowerupMultiplier(state);
	const totalMultiplier = powerupMultiplier * sprintTier.current.multiplier;
	const powerupScore = config.scoreValue * totalMultiplier;
	state.score += powerupScore;
	pixiUI.scoreDisplay.setScore(state.score);

	// Grant +5 segments bonus for collecting any powerup
	state.segmentCount += POWERUP_SEGMENT_BONUS;
	state.playerWorm.setSegmentCount(state.segmentCount);
	const newRadius = calculateWormRadius(state.score);
	state.playerWorm.setRadius(newRadius);
	state.stats.maxLength = Math.max(state.stats.maxLength, state.segmentCount);

	// Helper to add time to a powerup (stacks up to MAX_TIME)
	const addPowerupTime = (powerupType: PowerupType): void => {
		const existing = state.activePowerups.get(powerupType);
		const currentTime = existing?.remainingTime ?? 0;
		const newTime = Math.min(currentTime + ADD_TIME, MAX_TIME);
		state.activePowerups.set(powerupType, {
			type: powerupType,
			remainingTime: newTime,
			totalDuration: MAX_TIME,
		});
	};

	// Helper to set override powerup (fresh 30s, not stacking)
	const setOverride = (overrideType: PowerupType.MULTIPLIER_10X | PowerupType.MULTIPLIER_15X): void => {
		// If we have 6x active, pause it
		const existing6x = state.activePowerups.get(PowerupType.MULTIPLIER_6X);
		if (existing6x && !state.paused6x) {
			state.paused6x = {
				remainingTime: existing6x.remainingTime,
				totalDuration: existing6x.totalDuration,
			};
			state.activePowerups.delete(PowerupType.MULTIPLIER_6X);
		}

		// Remove any existing override
		if (state.activeOverride && state.activeOverride !== overrideType) {
			state.activePowerups.delete(state.activeOverride);
		}

		// Set fresh 30s for override (does NOT stack)
		state.activePowerups.set(overrideType, {
			type: overrideType,
			remainingTime: ADD_TIME, // Fresh 30 seconds
			totalDuration: ADD_TIME,
		});
		state.activeOverride = overrideType;
	};

	switch (type) {
		// BASE TIER - Stack with each other (2x and 4x can both be active)
		case PowerupType.MULTIPLIER_2X:
		case PowerupType.MULTIPLIER_4X:
			addPowerupTime(type);
			break;

		// FINAL SLOT - Only one 6x can be active (but it stacks time)
		case PowerupType.MULTIPLIER_6X:
			// If override is active, add to paused 6x instead
			if (state.activeOverride) {
				if (state.paused6x) {
					state.paused6x.remainingTime = Math.min(
						state.paused6x.remainingTime + ADD_TIME,
						MAX_TIME
					);
				} else {
					state.paused6x = { remainingTime: ADD_TIME, totalDuration: MAX_TIME };
				}
			} else {
				addPowerupTime(type);
			}
			break;

		// OVERRIDE - Temporarily replaces Final Slot (fresh 30s, no stacking)
		case PowerupType.MULTIPLIER_10X:
			setOverride(PowerupType.MULTIPLIER_10X);
			break;

		case PowerupType.MULTIPLIER_15X:
			setOverride(PowerupType.MULTIPLIER_15X);
			break;

		case PowerupType.MAGNET:
			state.magnetActive = true;
			addPowerupTime(type);
			break;

		case PowerupType.SPEED:
			// Speed now timed like other powerups, but also affects speed stacks
			if (state.speedStacks < POWERUP_SYSTEM.MAX_SPEED_STACKS) {
				state.speedStacks++;
				state.speed = DEFAULT_WORM_SPEED * (1 + state.speedStacks * POWERUP_SYSTEM.SPEED_BONUS_PER_STACK);
			}
			addPowerupTime(type);
			break;

		case PowerupType.AGILITY:
			state.agilityActive = true;
			addPowerupTime(type);
			break;

		case PowerupType.VISION:
			state.visionActive = true;
			state.fogOfWar.setVisionPowerupActive(true);
			addPowerupTime(type);
			break;
	}

	// Update the PowerupBar UI
	updatePowerupBarUI(state, pixiUI);
}

/**
 * Update active powerup timers and remove expired ones
 */
function updateActivePowerups(state: OfflineGameState, dt: number, pixiUI: PixiUI): void {
	const expiredPowerups: PowerupType[] = [];

	state.activePowerups.forEach((powerup, type) => {
		powerup.remainingTime -= dt;

		if (powerup.remainingTime <= 0) {
			expiredPowerups.push(type);
		}
	});

	// Remove expired powerups and reset their effects
	for (const type of expiredPowerups) {
		state.activePowerups.delete(type);

		switch (type) {
			// Base Tier multipliers - just remove, no special handling
			case PowerupType.MULTIPLIER_2X:
			case PowerupType.MULTIPLIER_4X:
				break;

			// Final Slot - just remove
			case PowerupType.MULTIPLIER_6X:
				break;

			// Override multipliers - restore paused 6x if exists
			case PowerupType.MULTIPLIER_10X:
			case PowerupType.MULTIPLIER_15X:
				state.activeOverride = null;

				// Restore paused 6x if we had one
				if (state.paused6x && state.paused6x.remainingTime > 0) {
					state.activePowerups.set(PowerupType.MULTIPLIER_6X, {
						type: PowerupType.MULTIPLIER_6X,
						remainingTime: state.paused6x.remainingTime,
						totalDuration: state.paused6x.totalDuration,
					});
					state.paused6x = null;
				}
				break;

			case PowerupType.MAGNET:
				state.magnetActive = false;
				break;

			case PowerupType.AGILITY:
				state.agilityActive = false;
				break;

			case PowerupType.SPEED:
				state.speedStacks = 0;
				state.speed = DEFAULT_WORM_SPEED;
				break;

			case PowerupType.VISION:
				state.visionActive = false;
				state.fogOfWar.setVisionPowerupActive(false);
				break;
		}
	}

	// Update UI if anything changed
	if (expiredPowerups.length > 0) {
		updatePowerupBarUI(state, pixiUI);
	}
}

/**
 * Update the PowerupBar UI with current active powerups
 */
function updatePowerupBarUI(state: OfflineGameState, pixiUI: PixiUI): void {
	const activePowerupList: ActivePowerup[] = [];

	state.activePowerups.forEach((powerup) => {
		activePowerupList.push({
			type: powerup.type,
			remainingTime: powerup.remainingTime,
			totalDuration: powerup.totalDuration,
			// Add stackCount for speed powerup display
			...(powerup.type === PowerupType.SPEED && { stackCount: state.speedStacks }),
		});
	});

	pixiUI.powerupBar.setActivePowerups(activePowerupList);

	// Show powerup bar if there are active powerups
	if (activePowerupList.length > 0) {
		pixiUI.powerupBar.show();
	} else {
		pixiUI.powerupBar.hide();
	}
}

/**
 * Update shrinking zone state (Battle Royale mode)
 * Handles phase transitions and zone shrinking
 */
function updateShrinkingZone(state: OfflineGameState, dt: number): void {
	const shrinking = state.shrinkingState;
	if (!shrinking?.enabled) return;

	const config = state.testModeConfig?.shrinkingConfig;
	if (!config) return;

	shrinking.phaseTimer += dt;

	switch (shrinking.phase) {
		case ShrinkingPhase.WAITING:
			// Wait for initial delay before starting
			if (shrinking.phaseTimer >= config.initialDelay) {
				shrinking.phase = ShrinkingPhase.WARNING;
				shrinking.phaseTimer = 0;
				shrinking.warningTimer = config.warningDuration;
				// Calculate target bounds for next shrink
				calculateNextShrinkTarget(shrinking, config);
			}
			break;

		case ShrinkingPhase.WARNING:
			// Countdown warning before shrink
			shrinking.warningTimer = Math.max(0, config.warningDuration - shrinking.phaseTimer);
			if (shrinking.phaseTimer >= config.warningDuration) {
				shrinking.phase = ShrinkingPhase.SHRINKING;
				shrinking.phaseTimer = 0;
			}
			break;

		case ShrinkingPhase.SHRINKING:
			// Interpolate bounds during shrink
			const shrinkProgress = Math.min(1, shrinking.phaseTimer / config.shrinkDuration);
			interpolateBounds(shrinking, shrinkProgress);

			if (shrinking.phaseTimer >= config.shrinkDuration) {
				// Shrink complete - copy target to current
				shrinking.currentBounds = { ...shrinking.targetBounds };
				shrinking.shrinkIndex++;

				// Check if we've reached the final phase
				if (shrinking.shrinkIndex >= config.shrinkPhases) {
					shrinking.phase = ShrinkingPhase.FINISHED;
				} else {
					shrinking.phase = ShrinkingPhase.STABLE;
					shrinking.phaseTimer = 0;
				}
			}
			break;

		case ShrinkingPhase.STABLE:
			// Wait between shrinks
			if (shrinking.phaseTimer >= config.shrinkInterval) {
				shrinking.phase = ShrinkingPhase.WARNING;
				shrinking.phaseTimer = 0;
				shrinking.warningTimer = config.warningDuration;
				calculateNextShrinkTarget(shrinking, config);
			}
			break;

		case ShrinkingPhase.FINISHED:
			// Zone at minimum size - no more updates needed
			break;
	}
}

/**
 * Calculate next shrink target bounds
 */
function calculateNextShrinkTarget(
	shrinking: NonNullable<OfflineGameState['shrinkingState']>,
	config: NonNullable<TestModeConfig['shrinkingConfig']>
): void {
	const { currentBounds } = shrinking;

	// Simple shrink: reduce to 85% of current size each phase
	const shrinkFactor = 0.85;
	const targetWidth = currentBounds.width * shrinkFactor;
	const targetHeight = currentBounds.height * shrinkFactor;

	// Random offset for the new center (within reasonable bounds)
	const maxOffset = Math.min(currentBounds.width, currentBounds.height) * 0.1;
	const offsetX = (Math.random() - 0.5) * maxOffset;
	const offsetY = (Math.random() - 0.5) * maxOffset;

	// Ensure we don't go below minimum area
	const minDim = Math.sqrt(config.minimumAreaPercent) * 500; // Minimum ~200 for 15% area

	shrinking.targetBounds = {
		x: currentBounds.x + offsetX,
		y: currentBounds.y + offsetY,
		width: Math.max(targetWidth, minDim),
		height: Math.max(targetHeight, minDim),
	};
}

/**
 * Interpolate between current and target bounds during shrinking
 */
function interpolateBounds(
	shrinking: NonNullable<OfflineGameState['shrinkingState']>,
	progress: number
): void {
	// Use eased progress for smoother shrinking
	const eased = 1 - Math.pow(1 - progress, 2); // Ease out quad

	shrinking.currentBounds.x = lerp(shrinking.currentBounds.x, shrinking.targetBounds.x, eased * 0.1);
	shrinking.currentBounds.y = lerp(shrinking.currentBounds.y, shrinking.targetBounds.y, eased * 0.1);
	shrinking.currentBounds.width = lerp(shrinking.currentBounds.width, shrinking.targetBounds.width, eased * 0.1);
	shrinking.currentBounds.height = lerp(shrinking.currentBounds.height, shrinking.targetBounds.height, eased * 0.1);
}

/**
 * Linear interpolation helper
 */
function lerp(a: number, b: number, t: number): number {
	return a + (b - a) * t;
}

/**
 * Determine essence type based on segment number (1-indexed)
 * - Every 10th segment -> Large Essence (+5)
 * - Every 5th segment (but not 10th) -> Medium Essence (+3)
 * - All others -> Small Essence (+1)
 */
function getEssenceType(segmentNumber: number): EssenceType {
	if (segmentNumber % ESSENCE_LARGE_INTERVAL === 0) {
		return EssenceType.LARGE;
	}
	if (segmentNumber % ESSENCE_MEDIUM_INTERVAL === 0) {
		return EssenceType.MEDIUM;
	}
	return EssenceType.SMALL;
}

/**
 * Get essence properties based on type
 */
function getEssenceProperties(type: EssenceType): { color: number; sizeMultiplier: number; value: number } {
	switch (type) {
		case EssenceType.LARGE:
			return { color: ESSENCE_LARGE_COLOR, sizeMultiplier: ESSENCE_LARGE_SIZE, value: ESSENCE_LARGE_VALUE };
		case EssenceType.MEDIUM:
			return { color: ESSENCE_MEDIUM_COLOR, sizeMultiplier: ESSENCE_MEDIUM_SIZE, value: ESSENCE_MEDIUM_VALUE };
		case EssenceType.SMALL:
		default:
			return { color: ESSENCE_SMALL_COLOR, sizeMultiplier: ESSENCE_SMALL_SIZE, value: ESSENCE_SMALL_VALUE };
	}
}

/**
 * Spawn death food (essence) from a dead worm's segments
 * Uses tiered essence system:
 * - Small Essence (+1): Blue droplet, default drop
 * - Medium Essence (+3): Larger, brighter, every 5th segment
 * - Large Essence (+5): Glowing orb, every 10th segment
 *
 * Example: 50 segment worm dies:
 * - 40x Small Essence (+1) = 40
 * - 8x Medium Essence (+3) = 24 (segments 5,15,25,35,45)
 * - 5x Large Essence (+5) = 25 (segments 10,20,30,40,50)
 * - Total: 89 value (bonus for hunting!)
 *
 * For large worms, uses stepping to limit food count while maintaining visual density
 */
function spawnDeathFood(
	state: OfflineGameState,
	wormSegments: Array<{ x: number; y: number; radius: number }>
): void {
	const { foodRenderer } = state;
	const segmentCount = wormSegments.length;

	// Cap max food items spawned to prevent performance issues
	// Use stepping for large worms with scaled-up food to compensate
	let step = 1;
	let radiusMultiplier = 1.0;
	const MAX_DEATH_FOOD = 2000; // Max food items from a single death

	if (segmentCount > MAX_DEATH_FOOD) {
		step = Math.ceil(segmentCount / MAX_DEATH_FOOD);
		radiusMultiplier = Math.sqrt(step); // Scale radius to compensate for fewer items
	}

	// Spawn essence at stepped segment positions
	for (let i = 0; i < segmentCount; i += step) {
		const seg = wormSegments[i];
		if (!seg) continue;

		// Segment number is 1-indexed for essence type calculation
		const segmentNumber = i + 1;

		// Determine essence type based on segment position
		const essenceType = getEssenceType(segmentNumber);
		const { color, sizeMultiplier, value } = getEssenceProperties(essenceType);

		// Small random offset for visual variety
		const offsetX = (Math.random() - 0.5) * 20;
		const offsetY = (Math.random() - 0.5) * 20;

		// Food radius based on segment size and essence type
		// Scale up for stepped spawning
		const baseRadius = Math.min(FOOD_RADIUS * 1.5, seg.radius * 0.5);
		const foodRadius = baseRadius * sizeMultiplier * radiusMultiplier;

		foodRenderer.addFood({
			id: `essence-${Date.now()}-${i}`,
			x: seg.x + offsetX,
			y: seg.y + offsetY,
			radius: foodRadius,
			color,
			essenceType,
			value,
		});
	}

	// Merge nearby essence into feasts after all drops are placed
	// This creates exciting high-value targets from clustered death drops!
	// Only merge if we spawned enough segments for potential feasts
	if (segmentCount >= 10) {
		foodRenderer.mergeNearbyEssence(60, 5);
	}
}

/**
 * Collision result for a single worm
 */
interface WormCollisionResult {
	died: boolean;
	killedBy: string | null;
	killerIndex: number | null; // -1 for player, index for AI, null for boundary
}

/**
 * Full collision check results
 */
interface CollisionResults {
	player: WormCollisionResult;
	aiDeaths: Array<{ index: number; killedBy: string | null }>;
}

/**
 * Skip first N segments when checking collision (prevents unfair close kills)
 */
const COLLISION_SKIP_SEGMENTS = 5;

/**
 * Get collision check step based on segment count
 * For very long worms, skip some segments to maintain performance
 */
function getCollisionStep(segmentCount: number): number {
	// Scale collision detection stepping for 50k+ segment worms
	if (segmentCount > 40000) return 25;
	if (segmentCount > 25000) return 16;
	if (segmentCount > 15000) return 12;
	if (segmentCount > 10000) return 8;
	if (segmentCount > 5000) return 5;
	if (segmentCount > 2000) return 3;
	if (segmentCount > 1000) return 2;
	return 1;
}

/**
 * Check ALL collisions in offline mode - applies same rules to ALL worms
 * Rules:
 * 1. Head hits another worm's body = death (body owner gets kill)
 * 2. Head hits boundary = death
 * 3. Head-to-head = both die
 */
function checkAllOfflineCollisions(state: OfflineGameState): CollisionResults {
	const results: CollisionResults = {
		player: { died: false, killedBy: null, killerIndex: null },
		aiDeaths: [],
	};

	const playerHead = state.playerWorm.getHeadPosition();
	const playerRadius = state.playerWorm.getRadius();
	const playerSegments = state.playerWorm.getSegmentPositions();

	// =========================================================================
	// 1. CHECK PLAYER DEATH CONDITIONS
	// =========================================================================

	// 1a. Player vs Boundary
	const playerDistFromCenter = Math.sqrt(playerHead.x * playerHead.x + playerHead.y * playerHead.y);
	if (playerDistFromCenter > OFFLINE_WORLD_RADIUS - playerRadius) {
		results.player = { died: true, killedBy: null, killerIndex: null };
	}

	// 1b. Player head vs AI bodies (skip first N segments)
	if (!results.player.died) {
		for (let i = 0; i < state.otherWorms.length; i++) {
			const aiWorm = state.otherWorms[i];
			if (!aiWorm) continue;

			const aiSegments = aiWorm.getSegmentPositions();
			const step = getCollisionStep(aiSegments.length);

			// Skip first N segments to prevent unfair close collision
			for (let segIdx = COLLISION_SKIP_SEGMENTS; segIdx < aiSegments.length; segIdx += step) {
				const seg = aiSegments[segIdx];
				if (!seg) continue;

				const dx = playerHead.x - seg.x;
				const dy = playerHead.y - seg.y;
				const dist = Math.sqrt(dx * dx + dy * dy);

				// Increase collision radius slightly when skipping segments
				const collisionRadius = playerRadius + seg.radius * (step > 1 ? 1.3 : 1);
				if (dist < collisionRadius) {
					const aiName = state.aiNames[i] ?? `Bot ${i + 1}`;
					results.player = { died: true, killedBy: aiName, killerIndex: i };
					break;
				}
			}
			if (results.player.died) break;
		}
	}

	// =========================================================================
	// 2. CHECK AI DEATH CONDITIONS
	// =========================================================================

	for (let i = 0; i < state.otherWorms.length; i++) {
		const aiWorm = state.otherWorms[i];
		if (!aiWorm) continue;

		const aiHead = aiWorm.getHeadPosition();
		const aiRadius = aiWorm.getRadius();
		let aiDied = false;
		let aiKilledBy: string | null = null;

		// 2a. AI vs Boundary
		const aiDistFromCenter = Math.sqrt(aiHead.x * aiHead.x + aiHead.y * aiHead.y);
		if (aiDistFromCenter > OFFLINE_WORLD_RADIUS - aiRadius) {
			aiDied = true;
			aiKilledBy = null; // Killed by boundary
		}

		// 2b. AI head vs Player body (player gets the kill!)
		if (!aiDied && !state.playerDead) {
			const playerStep = getCollisionStep(playerSegments.length);
			// Skip first N segments
			for (let segIdx = COLLISION_SKIP_SEGMENTS; segIdx < playerSegments.length; segIdx += playerStep) {
				const seg = playerSegments[segIdx];
				if (!seg) continue;

				const dx = aiHead.x - seg.x;
				const dy = aiHead.y - seg.y;
				const dist = Math.sqrt(dx * dx + dy * dy);

				const collisionRadius = aiRadius + seg.radius * (playerStep > 1 ? 1.3 : 1);
				if (dist < collisionRadius) {
					aiDied = true;
					aiKilledBy = 'You'; // Player killed this AI
					break;
				}
			}
		}

		// 2c. AI head vs other AI bodies
		if (!aiDied) {
			for (let j = 0; j < state.otherWorms.length; j++) {
				if (i === j) continue; // Skip self

				const otherWorm = state.otherWorms[j];
				if (!otherWorm) continue;

				const otherSegments = otherWorm.getSegmentPositions();
				const otherStep = getCollisionStep(otherSegments.length);

				// Skip first N segments
				for (let segIdx = COLLISION_SKIP_SEGMENTS; segIdx < otherSegments.length; segIdx += otherStep) {
					const seg = otherSegments[segIdx];
					if (!seg) continue;

					const dx = aiHead.x - seg.x;
					const dy = aiHead.y - seg.y;
					const dist = Math.sqrt(dx * dx + dy * dy);

					const collisionRadius = aiRadius + seg.radius * (otherStep > 1 ? 1.3 : 1);
					if (dist < collisionRadius) {
						aiDied = true;
						aiKilledBy = state.aiNames[j] ?? `Bot ${j + 1}`;
						break;
					}
				}
				if (aiDied) break;
			}
		}

		// 2d. Head-to-head collision with player
		if (!aiDied && !state.playerDead) {
			const dx = aiHead.x - playerHead.x;
			const dy = aiHead.y - playerHead.y;
			const dist = Math.sqrt(dx * dx + dy * dy);

			if (dist < aiRadius + playerRadius) {
				// Both die in head-to-head
				aiDied = true;
				aiKilledBy = 'You';
				if (!results.player.died) {
					const aiName = state.aiNames[i] ?? `Bot ${i + 1}`;
					results.player = { died: true, killedBy: aiName, killerIndex: i };
				}
			}
		}

		if (aiDied) {
			results.aiDeaths.push({ index: i, killedBy: aiKilledBy });
		}
	}

	return results;
}

/**
 * Setup input handlers for offline mode
 */
function setupOfflineInputHandlers(game: Game, state: OfflineGameState, pixiUI: PixiUI): void {
	const canvas = game.app.canvas;
	const { mobileControls } = pixiUI;

	// Start mobile controls if enabled
	if (state.mobileControlsEnabled) {
		mobileControls.start(canvas);

		// Hide keyboard control hints on mobile (they have touch buttons instead)
		pixiUI.controlHints.hide();

		// Set up mobile controls state callback
		mobileControls.onStateChange((mobileState) => {
			// Update joystick state
			state.mobileJoystickAngle = mobileState.joystickAngle;
			state.mobileJoystickForce = mobileState.joystickForce;

			// Update sprint state from mobile button
			if (mobileState.sprinting !== state.boosting) {
				if (mobileState.sprinting) {
					SoundManager.getInstance().play(SoundType.BOOST_START);
				} else {
					SoundManager.getInstance().play(SoundType.BOOST_END);
				}
				state.boosting = mobileState.sprinting;
			}

			// Update crawl state from mobile button
			state.isSlowCrawl = mobileState.crawling;
		});
	}

	// Mouse move handler - stores SCREEN position (world pos recalculated each frame)
	const handleMouseMove = (clientX: number, clientY: number): void => {
		// Store screen position (world position is recalculated each frame in updateOfflinePlayerWorm)
		state.mouseScreenPos.x = clientX;
		state.mouseScreenPos.y = clientY;
	};

	// Mouse events
	canvas.addEventListener('mousemove', (e: MouseEvent) => {
		handleMouseMove(e.clientX, e.clientY);
	});

	canvas.addEventListener('mousedown', (e: MouseEvent) => {
		if (e.button === BOOST_MOUSE_BUTTON) {
			if (!state.boosting) {
				SoundManager.getInstance().play(SoundType.BOOST_START);
			}
			state.boosting = true;
		}
	});

	canvas.addEventListener('mouseup', (e: MouseEvent) => {
		if (e.button === BOOST_MOUSE_BUTTON) {
			if (state.boosting) {
				SoundManager.getInstance().play(SoundType.BOOST_END);
			}
			state.boosting = false;
		}
	});

	canvas.addEventListener('mouseleave', () => {
		if (state.boosting) {
			SoundManager.getInstance().play(SoundType.BOOST_END);
		}
		state.boosting = false;
	});

	// Touch events (skip if mobile controls are enabled - they handle touch separately)
	canvas.addEventListener('touchstart', (e: TouchEvent) => {
		if (state.mobileControlsEnabled) return; // Mobile controls handle touch
		e.preventDefault();
		const touch = e.touches[0];
		if (touch) {
			handleMouseMove(touch.clientX, touch.clientY);
			if (!state.boosting) {
				SoundManager.getInstance().play(SoundType.BOOST_START);
			}
			state.boosting = true;
		}
	}, { passive: false });

	canvas.addEventListener('touchmove', (e: TouchEvent) => {
		if (state.mobileControlsEnabled) return; // Mobile controls handle touch
		e.preventDefault();
		const touch = e.touches[0];
		if (touch) {
			handleMouseMove(touch.clientX, touch.clientY);
		}
	}, { passive: false });

	canvas.addEventListener('touchend', (e: TouchEvent) => {
		if (state.mobileControlsEnabled) return; // Mobile controls handle touch
		e.preventDefault();
		if (state.boosting) {
			SoundManager.getInstance().play(SoundType.BOOST_END);
		}
		state.boosting = false;
	}, { passive: false });

	// Keyboard for boost
	window.addEventListener('keydown', (e: KeyboardEvent) => {
		if (e.key === ' ') {
			if (!state.boosting) {
				SoundManager.getInstance().play(SoundType.BOOST_START);
			}
			state.boosting = true;
		}
	});

	window.addEventListener('keyup', (e: KeyboardEvent) => {
		if (e.key === ' ') {
			if (state.boosting) {
				SoundManager.getInstance().play(SoundType.BOOST_END);
			}
			state.boosting = false;
		}
	});

	// Keyboard for slow crawl (Shift key)
	window.addEventListener('keydown', (e: KeyboardEvent) => {
		if (e.key === 'Shift') {
			state.isSlowCrawl = true;
		}
	});

	window.addEventListener('keyup', (e: KeyboardEvent) => {
		if (e.key === 'Shift') {
			state.isSlowCrawl = false;
		}
	});

	// Mouse wheel for zoom
	canvas.addEventListener('wheel', (e: WheelEvent) => {
		e.preventDefault();
		const zoomDelta = e.deltaY > 0 ? -CAMERA_ZOOM_DELTA : CAMERA_ZOOM_DELTA;
		game.camera.adjustZoom(zoomDelta);
	}, { passive: false });
}

/**
 * Setup game loop updates
 */
function setupGameLoop(appState: AppState): void {
	const { game } = appState;

	// Override the game's update method
	const originalUpdate = game.update.bind(game);
	game.update = (dt: number): void => {
		// Call original update (camera, world)
		originalUpdate(dt);

		// Mode-specific updates
		if (appState.mode === GameMode.ONLINE && appState.onlineState) {
			updateOnlineGame(appState, dt);
		} else if (appState.mode === GameMode.OFFLINE && appState.offlineState) {
			updateOfflineGame(appState, dt);
		}

		// Update total entity counts
		const monitor = PerformanceMonitor.getInstance();
		if (appState.mode === GameMode.OFFLINE && appState.offlineState) {
			const state = appState.offlineState;
			let segments = state.playerWorm.getSegmentCount();
			state.otherWorms.forEach(w => segments += w.getSegmentCount());
			monitor.setMetric('Worms', state.otherWorms.length + 1);
			monitor.setMetric('Segments', segments);
			monitor.setMetric('Food', state.foodRenderer.getFoodCount());
			monitor.setMetric('Powerups', state.activePowerups.size);
		} else if (appState.mode === GameMode.ONLINE && appState.onlineState) {
			const state = appState.onlineState;
			const allWorms = state.stateSync.getAllWorms();
			let segments = 0;
			allWorms.forEach(w => segments += w.renderer.getSegmentCount());
			monitor.setMetric('Worms', allWorms.length);
			monitor.setMetric('Segments', segments);
			monitor.setMetric('Food', state.foodRenderer.getFoodCount());
		}
	};
}

/**
 * Update online game state
 */
function updateOnlineGame(appState: AppState, dt: number): void {
	const { game, pixiUI } = appState;
	const { stateSync, inputManager, foodRenderer, powerupRenderer, fogOfWar, particleSystem, textPopupSystem, trailSystem, combo } = appState.onlineState!;

	// Update combo decay timer
	combo.timeSinceKill += dt;

	// Get culling bounds for per-segment visibility optimization
	const cullingBounds = game.camera.getVisibleBounds();
	const cullingPadding = 200; // Extra padding to prevent pop-in
	const expandedBounds = {
		left: cullingBounds.left - cullingPadding,
		right: cullingBounds.right + cullingPadding,
		top: cullingBounds.top - cullingPadding,
		bottom: cullingBounds.bottom + cullingPadding,
	};

	// Update state synchronization (interpolation) with culling bounds
	stateSync.update(dt, expandedBounds);

	// Update food animation
	foodRenderer.update(dt);

	// Update powerup animation
	powerupRenderer.update(dt);

	// Update particle system (food collection effects, etc.)
	particleSystem.update(dt);

	// Update text popups (kill effects, etc.)
	textPopupSystem.update(dt);

	// Update trail system
	trailSystem.update(dt);

	// Add trail points for all worms
	stateSync.forEachWorm((wormData, wormId) => {
		if (!wormData.alive) return;
		const renderer = wormData.renderer;
		const headPos = renderer.getHeadPosition();
		const radius = renderer.getHeadRadius();
		const color = wormData.color;
		const boosting = wormData.boosting ?? false;
		const angle = wormData.angle ?? 0;
		trailSystem.addTrailPoint(wormId, headPos.x, headPos.y, radius, color, boosting, angle, dt);
	});

	// Get local worm for camera and input updates
	const localWorm = stateSync.getLocalWorm();
	let localPlayerPos: { x: number; y: number } | undefined;

	// Debug: Log local worm status once per second
	const now = Date.now();
	if (!appState.onlineState!._lastCameraDebug || now - appState.onlineState!._lastCameraDebug > 1000) {
		appState.onlineState!._lastCameraDebug = now;
		console.log('[updateOnlineGame] Camera debug', {
			hasLocalWorm: !!localWorm,
			alive: localWorm?.alive,
			headPos: localWorm?.renderer.getHeadPosition(),
			segmentCount: localWorm?.renderer.getSegmentCount(),
		});
	}

	if (localWorm && localWorm.alive) {
		const renderer = localWorm.renderer;
		const headPos = renderer.getHeadPosition();
		localPlayerPos = headPos;

		// Update fog of war with player position and size
		const segmentCount = renderer.getSegmentCount();
		fogOfWar.updatePlayerPosition(headPos.x, headPos.y);
		fogOfWar.updatePlayerSize(segmentCount);

		// Update Vision powerup state for fog of war
		const hasVision = stateSync.hasLocalPlayerPowerup(PowerupType.VISION);
		fogOfWar.setVisionPowerupActive(hasVision);

		// Apply fog of war visibility bounds to renderers
		const visRadius = fogOfWar.getVisibilityRadius();
		foodRenderer.setVisibleBounds(
			headPos.x - visRadius,
			headPos.x + visRadius,
			headPos.y - visRadius,
			headPos.y + visRadius
		);
		powerupRenderer.setVisibleBounds(
			headPos.x - visRadius,
			headPos.x + visRadius,
			headPos.y - visRadius,
			headPos.y + visRadius
		);

		// Apply fog of war to other worms (hide those outside visibility range)
		// Uses isWormVisible for danger sense: small worms detect large worms at 1.5x range
		const allWorms = stateSync.getAllWorms();
		allWorms.forEach(worm => {
			if (worm.isLocal) return; // Always show local worm
			const wormPos = worm.renderer.getHeadPosition();
			const wormSegments = worm.renderer.getSegmentCount();
			const visible = fogOfWar.isWormVisible(wormPos.x, wormPos.y, worm.renderer.getRadius(), wormSegments);
			worm.renderer.setVisible(worm.alive && visible);
		});

		// Update near miss system and check for close calls
		pixiUI.nearMissSystem.update(dt);
		const playerRadius = renderer.getRadius();
		const otherWormsForNearMiss = allWorms
			.filter(w => !w.isLocal && w.alive)
			.map(worm => ({
				segments: worm.renderer.getSegmentPositions().map(seg => ({
					x: seg.x,
					y: seg.y,
					radius: worm.renderer.getRadius(),
				})),
				isAlive: worm.alive,
			}));
		pixiUI.nearMissSystem.checkNearMiss(headPos.x, headPos.y, playerRadius, otherWormsForNearMiss);

		// Update revenge intel arrow (points toward killer after respawn)
		pixiUI.revengeIntelArrow.update(dt, headPos.x, headPos.y);

		// Update camera to follow player
		game.camera.setTarget(headPos.x, headPos.y);

		// Update camera zoom based on worm size (bigger worms see more)
		game.camera.updateZoomForSize(segmentCount);

		// Update input manager with player position
		inputManager.updatePlayerPosition(headPos.x, headPos.y);

		// Update world boundary warning
		game.world.updatePlayerPosition(headPos.x, headPos.y);

		// Apply screen shake for boundary warning
		game.camera.setBoundaryWarning(game.world.getWarningIntensity());

		// Update score display
		pixiUI.scoreDisplay.setScore(localWorm.score);

		// Fuel bar is hidden - sprint is completely free now
		// pixiUI.fuelBar.update(localWorm.fuel ?? 100);
	}

	// Update minimap with worm and food data (minimap sees all worms)
	const allWorms = stateSync.getAllWorms();
	const minimapWorms: MinimapWorm[] = allWorms
		.filter(w => w.alive)
		.map(w => ({
			id: w.id,
			x: w.renderer.getHeadPosition().x,
			y: w.renderer.getHeadPosition().y,
			color: w.color,
			isLocal: w.isLocal,
		}));

	const allFood = foodRenderer.getAllFood();
	const minimapFood: MinimapFood[] = allFood.map(f => ({ x: f.x, y: f.y, value: f.value ?? 1 }));

	pixiUI.minimap.update(dt, minimapFood, minimapWorms, localPlayerPos);

	// Update powerup bar
	pixiUI.powerupBar.update(dt);

	// Update score display animation
	pixiUI.scoreDisplay.update(dt);

	// Update kill score display animation
	pixiUI.killScoreDisplay.update(dt);
}

/**
 * Update offline game state
 */
function updateOfflineGame(appState: AppState, dt: number): void {
	const { game, pixiUI, deathUI } = appState;
	const state = appState.offlineState!;

	// Skip all updates when game is paused
	if (state.paused) {
		return;
	}

	// Update death effects even when dead (for particle animation)
	state.deathEffects.update(dt);

	// Update particle system (food collection effects, etc.)
	state.particleSystem.update(dt);

	// Update mobile controls (joystick and buttons animation)
	pixiUI.mobileControls.update(dt);

	// Update dying worm animations (fade-out)
	for (const dyingWorm of state.dyingWorms) {
		dyingWorm.updateDeathAnimation(dt);
	}

	// Update skin animations for all worms (animated patterns like Electric, Lava, etc.)
	// Pass culling bounds for per-segment visibility optimization
	const cullingBounds = game.camera.getVisibleBounds();
	const cullingPadding = 200; // Extra padding to prevent pop-in
	const expandedBounds = {
		left: cullingBounds.left - cullingPadding,
		right: cullingBounds.right + cullingPadding,
		top: cullingBounds.top - cullingPadding,
		bottom: cullingBounds.bottom + cullingPadding,
	};

	state.playerWorm.setCullingBounds(expandedBounds);
	state.playerWorm.updateSkinAnimation(dt);
	for (const aiWorm of state.otherWorms) {
		aiWorm.setCullingBounds(expandedBounds);
		aiWorm.updateSkinAnimation(dt);
	}

	// Track time alive (before death check)
	if (!state.playerDead) {
		state.stats.timeAlive += dt;
	}

	// Skip other updates if player is dead
	if (state.playerDead) {
		// Still update food animation so death drops look good
		state.foodRenderer.update(dt);
		return;
	}

	// Update player worm movement
	updateOfflinePlayerWorm(game, state, dt, pixiUI);

	// Update AI worms
	updateOfflineAIWorms(state, dt);

	// Check and respawn AI worms if count is low
	const wormsLayer = game.getLayer(GameLayer.WORMS);
	checkAIRespawn(state, wormsLayer, dt);

	// Check ALL collisions AFTER movement (universal rules for all worms)
	const collisionResults = checkAllOfflineCollisions(state);

	// =========================================================================
	// Handle AI deaths first (so their drops appear before we process player)
	// =========================================================================
	if (collisionResults.aiDeaths.length > 0) {
		// Process deaths in reverse order to avoid index shifting issues
		const sortedDeaths = [...collisionResults.aiDeaths].sort((a, b) => b.index - a.index);

		for (const death of sortedDeaths) {
			const aiWorm = state.otherWorms[death.index];
			if (!aiWorm) continue;

			const aiSegments = aiWorm.getSegmentPositions();
			const aiHead = aiWorm.getHeadPosition();
			const aiColor = WORM_COLORS[(death.index + 1) % WORM_COLORS.length] ?? 0x4ecdc4;

			// Track kills if player killed this AI
			if (death.killedBy === 'You') {
				state.stats.kills++;

				// Determine AI type from personality for kill tracking
				const aiPersonality = state.aiPersonalities[death.index];
				let killType: KillType = 'collector'; // Default
				if (aiPersonality) {
					const personalityType = aiPersonality.type.toLowerCase();
					if (personalityType === 'legendary') {
						killType = 'leviathan';
					} else if (personalityType === 'apex' || personalityType === 'hunter' || personalityType === 'collector') {
						killType = personalityType as KillType;
					}
				}
				state.killCounts[killType]++;
				pixiUI.killScoreDisplay.addKill(killType);

				// Play triumphant kill sound
				SoundManager.getInstance().play(SoundType.KILL);
			}

			// Particle explosion from AI's head
			state.deathEffects.spawnExplosion(aiHead.x, aiHead.y, aiColor, 30);

			// Drop food at AI's segment positions
			spawnDeathFood(state, aiSegments);

			// Start death animation instead of immediately removing
			// The worm will fade out over 1 second
			aiWorm.startDeathAnimation(() => {
				// After animation completes, remove from container
				const wormsLayer = game.getLayer(GameLayer.WORMS);
				wormsLayer.removeChild(aiWorm.container);
				aiWorm.destroy();

				// Remove from dying worms list
				const dyingIndex = state.dyingWorms.indexOf(aiWorm);
				if (dyingIndex !== -1) {
					state.dyingWorms.splice(dyingIndex, 1);
				}
			}, 1000);

			// Add to dying worms list (for animation updates)
			state.dyingWorms.push(aiWorm);

			// Remove from active arrays (no longer participates in collisions/AI logic)
			state.otherWorms.splice(death.index, 1);
			state.aiNames.splice(death.index, 1);
			state.aiSegmentCounts.splice(death.index, 1);
			state.aiWanderAngles.splice(death.index, 1);
			state.aiWanderTimers.splice(death.index, 1);
			// Clean up AI brain state arrays
			state.aiStates.splice(death.index, 1);
			state.aiThinkTimers.splice(death.index, 1);
			state.aiPersonalities.splice(death.index, 1);
			state.aiBoosting.splice(death.index, 1);
			state.aiCrawling.splice(death.index, 1);
		}
	}

	// =========================================================================
	// Handle player death
	// =========================================================================
	if (collisionResults.player.died) {
		state.playerDead = true;
		state.lastKillerIndex = collisionResults.player.killerIndex;

		// Get player segments BEFORE hiding the worm (for death drops)
		const playerSegments = state.playerWorm.getSegmentPositions();
		const playerHead = state.playerWorm.getHeadPosition();
		const playerColor = WORM_COLORS[0] ?? 0xff6b6b;

		// Hide the worm
		state.playerWorm.setVisible(false);

		// === DEATH EFFECTS ===
		// 1. Screen shake - intense!
		game.camera.shake(1.0);

		// 2. Death sound
		SoundManager.getInstance().play(SoundType.DEATH);

		// 3. Particle explosion from head
		state.deathEffects.spawnExplosion(playerHead.x, playerHead.y, playerColor, 50);

		// 4. Death food drops - spawn food at each segment position
		spawnDeathFood(state, playerSegments);

		// Show death UI with stats
		const killedBy = collisionResults.player.killedBy;
		const earnedPoints = calculateEarnedPoints(state.killCounts, state.nightmareMode);
		deathUI.show(killedBy, state.score, 1000, {
			timeAlive: state.stats.timeAlive,
			foodEaten: state.stats.foodEaten,
			maxLength: state.stats.maxLength,
			kills: state.stats.kills,
			earnedPoints,
		});
		return;
	}

	// Update food
	state.foodRenderer.update(dt);

	// Update game time for feast system
	state.gameTime += dt;

	// Periodic feast merge check (merges clustered food into exciting high-value feasts)
	state.feastMergeTimer += dt;
	if (state.feastMergeTimer >= FEAST_SYSTEM.MERGE_CHECK_INTERVAL) {
		state.feastMergeTimer = 0;
		const feastTiers = state.foodRenderer.mergeAllFood(state.gameTime);
		// Play sounds for each feast tier created (higher tiers are more exciting)
		const soundManager = SoundManager.getInstance();
		for (const tier of feastTiers) {
			switch (tier) {
				case FeastTier.MEAL:
					soundManager.play(SoundType.FEAST_MEAL);
					break;
				case FeastTier.DINNER:
					soundManager.play(SoundType.FEAST_DINNER);
					break;
				case FeastTier.FEAST:
					soundManager.play(SoundType.FEAST_FEAST);
					break;
				case FeastTier.BANQUET:
					soundManager.play(SoundType.FEAST_BANQUET);
					break;
				case FeastTier.GOLDEN_BUFFET:
					soundManager.play(SoundType.FEAST_GOLDEN);
					break;
				case FeastTier.MYTHICAL_FEAST:
					soundManager.play(SoundType.FEAST_MYTHICAL);
					break;
			}
		}
	}

	// Update powerups
	state.powerupRenderer.update(dt);

	// Update active powerup timers
	updateActivePowerups(state, dt, pixiUI);

	// Powerup spawning - use preset values if in test mode, otherwise use defaults
	const ratePreset = state.testModeConfig?.powerupRatePreset;
	const targetPowerupCount = ratePreset?.maxCount ?? calculatePowerupCount(OFFLINE_WORLD_RADIUS);
	const spawnRate = ratePreset?.spawnRate ?? POWERUP_SYSTEM.SPAWN_RATE;
	state.powerupSpawnTimer += dt;
	if (state.powerupSpawnTimer >= 1.0 / spawnRate) {
		state.powerupSpawnTimer = 0;
		if (state.powerupRenderer.getPowerupCount() < targetPowerupCount) {
			const newPowerup = spawnRandomPowerup(state.nextPowerupId++);
			state.powerupRenderer.addPowerup(newPowerup);
			state.powerupSpawnTimes.set(newPowerup.id, state.gameTime);
		}
	}

	// Powerup decay - half-life of 5 minutes (300 seconds)
	// Each powerup has a 50% chance of despawning after 5 minutes
	const POWERUP_HALF_LIFE = 300; // 5 minutes in seconds
	const DECAY_CHECK_INTERVAL = 10; // Check every 10 seconds
	state.powerupDecayTimer += dt;
	if (state.powerupDecayTimer >= DECAY_CHECK_INTERVAL) {
		state.powerupDecayTimer = 0;
		const allPowerups = state.powerupRenderer.getAllPowerups();
		const powerupsToRemove: string[] = [];

		for (const powerup of allPowerups) {
			const spawnTime = state.powerupSpawnTimes.get(powerup.id) ?? 0;
			const age = state.gameTime - spawnTime;

			if (age >= POWERUP_HALF_LIFE) {
				// Calculate decay probability based on age past half-life
				// After 5 min: 50% chance, 10 min: 75%, 15 min: 87.5%, etc.
				const halvingsElapsed = (age - POWERUP_HALF_LIFE) / POWERUP_HALF_LIFE;
				const survivalProbability = Math.pow(0.5, 1 + halvingsElapsed);
				const decayRoll = Math.random();

				if (decayRoll > survivalProbability) {
					powerupsToRemove.push(powerup.id);
				}
			}
		}

		// Remove decayed powerups
		for (const id of powerupsToRemove) {
			state.powerupRenderer.removePowerup(id);
			state.powerupSpawnTimes.delete(id);
		}
	}

	// Get player position for camera, FOW, etc.
	const headPos = state.playerWorm.getHeadPosition();

	// Update fog of war with player position, size, and sprint duration
	state.fogOfWar.updatePlayerPosition(headPos.x, headPos.y);
	state.fogOfWar.updatePlayerSize(state.segmentCount);
	state.fogOfWar.setSprintDuration(state.sprintTime);

	// Apply fog of war visibility bounds to food and powerup renderers
	const visRadius = state.fogOfWar.getVisibilityRadius();
	state.foodRenderer.setVisibleBounds(
		headPos.x - visRadius,
		headPos.x + visRadius,
		headPos.y - visRadius,
		headPos.y + visRadius
	);
	state.powerupRenderer.setVisibleBounds(
		headPos.x - visRadius,
		headPos.x + visRadius,
		headPos.y - visRadius,
		headPos.y + visRadius
	);

	// Apply fog of war to AI worms (hide those outside visibility range)
	// Uses isWormVisible for danger sense: small worms detect large worms at 1.5x range
	state.otherWorms.forEach(worm => {
		const wormPos = worm.getHeadPosition();
		const wormSegments = worm.getSegmentCount();
		const visible = state.fogOfWar.isWormVisible(wormPos.x, wormPos.y, worm.getRadius(), wormSegments);
		worm.setVisible(visible);
	});

	// Update near miss system and check for close calls
	pixiUI.nearMissSystem.update(dt);
	const playerRadius = state.playerWorm.getRadius();
	const otherWormsForNearMiss = state.otherWorms.map(worm => ({
		segments: worm.getSegmentPositions().map(seg => ({
			x: seg.x,
			y: seg.y,
			radius: worm.getRadius(),
		})),
		isAlive: true, // AI worms in otherWorms array are all alive
	}));
	pixiUI.nearMissSystem.checkNearMiss(headPos.x, headPos.y, playerRadius, otherWormsForNearMiss);

	// Update revenge intel arrow (points toward killer after respawn)
	pixiUI.revengeIntelArrow.update(dt, headPos.x, headPos.y);

	// Update world boundary warning
	game.world.updatePlayerPosition(headPos.x, headPos.y);

	// Apply screen shake for boundary warning
	game.camera.setBoundaryWarning(game.world.getWarningIntensity());

	// Update minimap with worm and food data (minimap sees all worms)
	const minimapWorms: MinimapWorm[] = [
		{
			id: 'player',
			x: headPos.x,
			y: headPos.y,
			color: WORM_COLORS[0] ?? 0xff6b6b,
			isLocal: true,
		},
		...state.otherWorms.map((worm, index) => ({
			id: `ai-${index}`,
			x: worm.getHeadPosition().x,
			y: worm.getHeadPosition().y,
			color: WORM_COLORS[(index + 1) % WORM_COLORS.length] ?? 0x4ecdc4,
			isLocal: false,
		})),
	];

	const allFood = state.foodRenderer.getAllFood();
	const minimapFood: MinimapFood[] = allFood.map(f => ({ x: f.x, y: f.y, value: f.value ?? 1 }));

	// Update shrinking zone state (Battle Royale mode)
	updateShrinkingZone(state, dt);

	// Update minimap with shrinking zone state
	if (state.shrinkingState?.enabled) {
		pixiUI.minimap.updateShrinkingZone({
			enabled: true,
			currentBounds: state.shrinkingState.currentBounds,
			targetBounds: state.shrinkingState.targetBounds,
			phase: state.shrinkingState.phase,
			warningTimer: state.shrinkingState.warningTimer,
		});
	}

	pixiUI.minimap.update(dt, minimapFood, minimapWorms, headPos);

	// Update leaderboard with player and AI scores
	const leaderboardEntries: Array<{ id: string; name: string; score: number; rank: number }> = [];

	// Add player
	leaderboardEntries.push({
		id: 'player',
		name: 'You',
		score: state.score,
		rank: 0,
	});

	// Add AI worms with estimated scores based on segment count
	state.otherWorms.forEach((worm, index) => {
		const aiScore = worm.getSegmentCount() * GROWTH.SCORE_PER_SEGMENT; // Score = segments * 2
		const aiName = state.aiNames[index] ?? `Bot ${index + 1}`;
		leaderboardEntries.push({
			id: `ai-${index}`,
			name: aiName,
			score: aiScore,
			rank: 0,
		});
	});

	// Sort by score descending and assign ranks
	leaderboardEntries.sort((a, b) => b.score - a.score);
	leaderboardEntries.forEach((entry, index) => {
		entry.rank = index + 1;
	});

	// Update leaderboard (top 10)
	pixiUI.leaderboard.update(leaderboardEntries.slice(0, 10));

	// Update powerup bar
	pixiUI.powerupBar.update(dt);

	// Update score display animation
	pixiUI.scoreDisplay.update(dt);

	// Update kill score display animation
	pixiUI.killScoreDisplay.update(dt);
}

/**
 * Update offline player worm based on mouse position with turn rate limiting
 * - Worm ALWAYS moves forward (never stops)
 * - Turn rate is limited (no instant 180 degree turns)
 * - Boosting reduces turn rate slightly
 */
function updateOfflinePlayerWorm(game: Game, state: OfflineGameState, dt: number, pixiUI: PixiUI): void {
	const { playerWorm, foodRenderer } = state;
	const headPos = playerWorm.getHeadPosition();

	// Recalculate world position from screen position EVERY FRAME
	// This ensures the target stays correct as the camera moves
	const worldPos = game.camera.screenToWorld(state.mouseScreenPos.x, state.mouseScreenPos.y);
	state.mouseWorldPos.x = worldPos.x;
	state.mouseWorldPos.y = worldPos.y;

	// FREE SPRINT: Worms with score under threshold sprint for free (comeback mechanic)
	const hasFreeSprint = state.score < FREE_SPRINT_SCORE_THRESHOLD;

	// Sprinting drops food from tail (no fuel limit - sprint as long as you want)
	// But you lose segments which become food for others
	// EXCEPTION: Free sprint for small worms (no segment cost when score < threshold)
	if (state.boosting && state.segmentCount > 3 && !hasFreeSprint) {
		// Accumulate time for segment drop (drop 1 segment per 0.5 seconds of sprinting)
		const SECONDS_PER_SEGMENT_DROP = 0.5;
		state.boostBurnAccumulator = (state.boostBurnAccumulator || 0) + dt;

		while (state.boostBurnAccumulator >= SECONDS_PER_SEGMENT_DROP && state.segmentCount > 3) {
			state.boostBurnAccumulator -= SECONDS_PER_SEGMENT_DROP;

			// Get tail position before removing segment
			const bodyPoints = playerWorm.getBodyPoints();
			const tailPos = bodyPoints[bodyPoints.length - 1];

			if (tailPos) {
				// Spawn food at tail position (marked as fresh sprint drop for merge immunity)
				foodRenderer.addFood({
					id: `boost-drop-${Date.now()}-${Math.random()}`,
					x: tailPos.x + (Math.random() - 0.5) * 10,
					y: tailPos.y + (Math.random() - 0.5) * 10,
					radius: FOOD_RADIUS,
					color: 0xffaa00, // Orange color for boost-dropped food
					value: 1,
					isFreshDrop: true,
					droppedAt: state.gameTime,
				});
			}

			// Remove segment from tail
			state.segmentCount--;
			state.score = Math.max(0, state.score - GROWTH.SCORE_PER_SEGMENT);
			playerWorm.setSegmentCount(state.segmentCount);

			// Update score display
			pixiUI.scoreDisplay.setScore(state.score);
		}
	} else if (!state.boosting) {
		// Reset accumulator when not boosting
		state.boostBurnAccumulator = 0;
	}

	// Fuel bar not used for sprint limitation (kept for visual feedback only)
	pixiUI.fuelBar.hide();

	// Handle crawl mode passive score generation (only when holding Shift)
	if (!state.isSlowCrawl || state.boosting) {
		// Reset crawl mode timer and accumulator when not crawling or boosting
		state.slowModeTimer = 0;
		state.slowModeScoreAccumulator = 0;
	} else {
		// Increment crawl mode timer (only when holding Shift and not boosting)
		state.slowModeTimer += dt;

		// Award score after activation delay
		if (state.slowModeTimer >= SLOW_MODE.ACTIVATION_DELAY) {
			// Accumulate score (passive growth while crawling)
			state.slowModeScoreAccumulator += SLOW_MODE.SCORE_PER_SECOND * dt;

			// Award whole points and grow worm
			const pointsToAward = Math.floor(state.slowModeScoreAccumulator);
			if (pointsToAward > 0) {
				state.score += pointsToAward;

				// Update score display
				pixiUI.scoreDisplay.setScore(state.score);

				// Accumulate growth points (2 points = 1 segment)
				state.growthAccumulator += pointsToAward;
				const segmentsToAdd = Math.floor(state.growthAccumulator / GROWTH.SCORE_PER_SEGMENT);
				state.growthAccumulator = state.growthAccumulator % GROWTH.SCORE_PER_SEGMENT;

				if (segmentsToAdd > 0) {
					const currentSegments = playerWorm.getSegmentCount();
					playerWorm.setSegmentCount(currentSegments + segmentsToAdd);
					state.segmentCount += segmentsToAdd;
				}

				state.slowModeScoreAccumulator -= pointsToAward;
			}
		}
	}

	// Handle sprint multiplier tracking
	if (state.boosting) {
		// Accumulate sprint time when boosting
		state.sprintTime += dt;
	} else {
		// Decay sprint time when not boosting (with grace period)
		if (state.sprintTime > SPRINT_MULTIPLIER.DECAY_GRACE_PERIOD) {
			state.sprintTime = Math.max(0, state.sprintTime - SPRINT_MULTIPLIER.DECAY_RATE * dt);
		}
	}

	// Update sprint gauge UI (pass score for free sprint indicator)
	pixiUI.sprintGauge.update(dt, state.boosting, state.sprintTime, state.score);

	// Update total multiplier display (sprint × powerup)
	const sprintTier = getSprintTier(state.sprintTime);
	const powerupMultiplier = calculatePowerupMultiplier(state);
	const totalMultiplier = sprintTier.current.multiplier * powerupMultiplier;
	pixiUI.scoreDisplay.setMultiplier(totalMultiplier);

	const boosting = state.boosting;

	// Calculate target angle from head to mouse position (or joystick for mobile)
	const dx = state.mouseWorldPos.x - headPos.x;
	const dy = state.mouseWorldPos.y - headPos.y;
	const distToMouse = Math.sqrt(dx * dx + dy * dy);

	// Determine turn rate based on movement state and agility powerup
	// Boosting reduces turn rate for balance (harder to maneuver at high speed)
	// Slow crawl increases turn rate for precise control
	// Agility powerup increases turn rate significantly
	let maxTurnRate = state.agilityActive
		? TURN_RATE_AGILITY
		: boosting
			? TURN_RATE_BOOSTING
			: state.isSlowCrawl
				? TURN_RATE_NORMAL * SLOW_CRAWL.TURN_RATE_MULTIPLIER
				: TURN_RATE_NORMAL;

	// ANTI-CIRCLING: Reduce turn rate when mouse is VERY close to prevent idle circling
	// Only affects when mouse is practically on top of the head (within 30 units)
	// Skip this for joystick mode since joystick controls don't have this issue
	const MIN_TURN_DISTANCE = 30;
	const useJoystick = state.mobileControlsEnabled &&
		MobileSettings.getInstance().isJoystickMode &&
		state.mobileJoystickForce > 0;
	if (!useJoystick && distToMouse < MIN_TURN_DISTANCE) {
		maxTurnRate *= distToMouse / MIN_TURN_DISTANCE;
	}

	// Calculate target angle: use joystick angle if mobile joystick is active, otherwise use mouse
	let targetAngle: number;
	if (useJoystick) {
		// Joystick mode: use joystick direction directly
		targetAngle = state.mobileJoystickAngle;
	} else {
		// Pointer/mouse mode: calculate angle from head to cursor
		targetAngle = distToMouse > 1 ? Math.atan2(dy, dx) : state.currentAngle;
	}

	// Apply turn rate limiting: gradually rotate toward target angle
	const angleDiff = MathUtils.normalizeAngle(targetAngle - state.currentAngle);
	const maxTurnThisTick = maxTurnRate * dt;
	const clampedTurn = Math.max(-maxTurnThisTick, Math.min(maxTurnThisTick, angleDiff));
	state.currentAngle = MathUtils.normalizeAngle(state.currentAngle + clampedTurn);

	// Calculate speed (with boost or slow crawl multipliers)
	const speed = boosting
		? state.speed * BOOST_SPEED_MULTIPLIER
		: state.isSlowCrawl
			? state.speed * SLOW_CRAWL.SPEED_MULTIPLIER
			: state.speed;

	// ALWAYS move forward in current direction (worm never stops)
	const moveAmount = speed * dt;
	const newX = headPos.x + Math.cos(state.currentAngle) * moveAmount;
	const newY = headPos.y + Math.sin(state.currentAngle) * moveAmount;

	// NOTE: Removed boundary clamping - collision system now handles boundary death
	// Update worm head position (collision check happens after this in updateOfflineGame)
	playerWorm.setDeltaTime(dt);
	playerWorm.updateHeadPosition(newX, newY);

	// Apply boost visual effect
	playerWorm.setBoostEffect(boosting);

	// Update eye expression based on game state
	// Priority: Happy (powerup) > Angry (long sprint) > Scared (low score) > Normal
	const hasPowerup = state.activePowerups.size > 0;
	playerWorm.setExpressionState(state.sprintTime, state.score, hasPowerup);

	// Calculate forward eating position (eating zone is at the front of the head)
	const playerHeadRadius = playerWorm.getRadius();
	const playerHeadRotation = playerWorm.getHeadRotation();
	const forwardOffset = playerHeadRadius * EATING_FORWARD_OFFSET;
	const eatingX = newX + Math.cos(playerHeadRotation) * forwardOffset;
	const eatingY = newY + Math.sin(playerHeadRotation) * forwardOffset;

	// Apply magnet effect - pull nearby food AND powerups toward player's eating zone
	if (state.magnetActive) {
		// Magnet range scales with head size (bigger worms = bigger magnet range)
		const magnetRadius = MAGNET_RADIUS + (playerHeadRadius * MAGNET_HEAD_SCALE);
		const pullSpeed = MAGNET_PULL_STRENGTH * dt;

		// Pull food toward eating zone
		const magnetFood = foodRenderer.getFoodNear(eatingX, eatingY, magnetRadius);
		for (const food of magnetFood) {
			const dx = eatingX - food.x;
			const dy = eatingY - food.y;
			const dist = Math.sqrt(dx * dx + dy * dy);

			if (dist > 0) {
				const moveX = (dx / dist) * pullSpeed;
				const moveY = (dy / dist) * pullSpeed;
				const newFoodX = dist > pullSpeed ? food.x + moveX : eatingX;
				const newFoodY = dist > pullSpeed ? food.y + moveY : eatingY;
				foodRenderer.updateFoodPosition(food.id, newFoodX, newFoodY);
			}
		}

		// Pull powerups toward eating zone (same behavior as food)
		const magnetPowerups = state.powerupRenderer.getPowerupsNear(eatingX, eatingY, magnetRadius);
		for (const powerup of magnetPowerups) {
			const dx = eatingX - powerup.x;
			const dy = eatingY - powerup.y;
			const dist = Math.sqrt(dx * dx + dy * dy);

			if (dist > 0) {
				const moveX = (dx / dist) * pullSpeed;
				const moveY = (dy / dist) * pullSpeed;
				const newPowerupX = dist > pullSpeed ? powerup.x + moveX : eatingX;
				const newPowerupY = dist > pullSpeed ? powerup.y + moveY : eatingY;
				state.powerupRenderer.updatePowerupPosition(powerup.id, newPowerupX, newPowerupY);
			}
		}
	}

	// Check for food collection at the forward eating position
	// Pickup radius scales with worm size: Head Radius + BASE_FOOD_PICKUP_OFFSET
	// Same formula for player and AI - fair for everyone
	const playerPickupRadius = playerHeadRadius + BASE_FOOD_PICKUP_OFFSET;
	const nearbyFood = foodRenderer.getFoodNear(eatingX, eatingY, playerPickupRadius);
	if (nearbyFood.length > 0) {
		let totalPointsGained = 0;
		let foodEaten = 0;

		for (const food of nearbyFood) {
			// Spawn particle effect at food position
			state.particleSystem.spawnFoodEffect(food.x, food.y, food.color);

			// Play eat sound
			SoundManager.getInstance().play(SoundType.EAT);

			// Collect the food
			foodRenderer.removeFood(food.id);

			// Score increases by food value (multiplied by powerup and sprint multipliers)
			// Essence drops have explicit value property, regular food uses radius as fallback
			const sprintTier = getSprintTier(state.sprintTime);
			const powerupMult = calculatePowerupMultiplier(state);
			const totalMultiplier = powerupMult * sprintTier.current.multiplier;
			const baseValue = food.value ?? Math.ceil(food.radius);
			const pointsGained = baseValue * totalMultiplier;
			totalPointsGained += pointsGained;
			foodEaten++;
		}

		// Update score (can be more than segment count for display)
		state.score += totalPointsGained;

		// Accumulate growth points (2 points = 1 segment)
		state.growthAccumulator += totalPointsGained;
		const segmentsToAdd = Math.floor(state.growthAccumulator / GROWTH.SCORE_PER_SEGMENT);
		state.growthAccumulator = state.growthAccumulator % GROWTH.SCORE_PER_SEGMENT;

		// Grow segments if accumulated enough points
		if (segmentsToAdd > 0) {
			state.segmentCount += segmentsToAdd;

			// Update worm size - grow segments and radius
			playerWorm.setSegmentCount(state.segmentCount);
			const newRadius = calculateWormRadius(state.score);
			playerWorm.setRadius(newRadius);

			// Update fog of war with new size
			state.fogOfWar.updatePlayerSize(state.segmentCount);
		}

		// Track stats
		state.stats.foodEaten += foodEaten;
		state.stats.maxLength = Math.max(state.stats.maxLength, state.segmentCount);

		// Update score display
		pixiUI.scoreDisplay.setScore(state.score);
	}

	// Check for powerup collection
	checkPowerupCollection(state, headPos.x, headPos.y, playerWorm.getRadius(), pixiUI);

	// Draw eating radius indicator (positioned at front of head)
	const headRotation = playerWorm.getHeadRotation();
	drawEatingRadius(state, headPos.x, headPos.y, playerWorm.getRadius(), headRotation);

	// Update camera to follow player
	game.camera.setTarget(headPos.x, headPos.y);

	// Update camera zoom based on worm size (bigger worms see more)
	game.camera.updateZoomForSize(state.segmentCount);
}

/**
 * Build the game state object for AI brain consumption
 */
function buildClientAIGameState(state: OfflineGameState): ClientAIGameState {
	// Build worm data for all AI worms
	const worms: ClientAIWormData[] = state.otherWorms.map((worm, index) => {
		const headPos = worm.getHeadPosition();
		const segments = worm.getSegmentPositions();
		const aiScore = worm.getSegmentCount() * GROWTH.SCORE_PER_SEGMENT;

		return {
			id: `ai_${index}`,
			index,
			isAI: true,
			alive: true,
			score: aiScore,
			angle: worm.getHeadRotation(),
			boosting: state.aiBoosting[index] ?? false,
			headX: headPos.x,
			headY: headPos.y,
			radius: worm.getRadius(),
			segments: segments.map(seg => ({ x: seg.x, y: seg.y, radius: worm.getRadius() })),
			aiState: state.aiStates[index] ?? AIState.HUNT_FOOD,
			aiThinkTimer: state.aiThinkTimers[index] ?? 0,
			personality: state.aiPersonalities[index] ?? AI_PERSONALITIES.Hunter,
		};
	});

	// Build player worm data
	let playerWorm: ClientAIWormData | null = null;
	if (!state.playerDead) {
		const playerHead = state.playerWorm.getHeadPosition();
		const playerSegments = state.playerWorm.getSegmentPositions();
		playerWorm = {
			id: 'player',
			index: -1,
			isAI: false,
			alive: true,
			score: state.score,
			angle: state.currentAngle,
			boosting: state.boosting,
			headX: playerHead.x,
			headY: playerHead.y,
			radius: state.playerWorm.getRadius(),
			segments: playerSegments.map(seg => ({ x: seg.x, y: seg.y, radius: state.playerWorm.getRadius() })),
			aiState: AIState.HUNT_FOOD,
			aiThinkTimer: 0,
			personality: AI_PERSONALITIES.Hunter,
		};
	}

	// Build food data - get food near the center of the world (large search radius)
	// For efficiency, we use the food renderer's spatial query
	const allFood = state.foodRenderer.getFoodNear(0, 0, OFFLINE_WORLD_RADIUS);
	const foods: ClientAIFoodData[] = allFood.map(food => ({
		id: food.id,
		x: food.x,
		y: food.y,
		// Use explicit value from essence, fallback to old heuristic for backwards compatibility
		value: food.value ?? (food.id.startsWith('essence-') ? 3 : 1),
		radius: food.radius ?? FOOD_RADIUS,
		fromDeath: food.id.startsWith('essence-'), // Essence drops are from death
	}));

	// Build powerup data
	const powerUps: ClientAIPowerUpData[] = state.powerupRenderer.getAllPowerups().map(pu => ({
		id: pu.id,
		x: pu.x,
		y: pu.y,
		radius: pu.radius,
		type: pu.powerupType,
	}));

	return {
		worms,
		playerWorm,
		foods,
		powerUps,
		worldRadius: OFFLINE_WORLD_RADIUS,
	};
}

/**
 * Update offline AI worms with sophisticated state-machine AI:
 * - 5-state machine: FLEE_EDGE, EVADE, COLLECT_POWERUP, HUNT_PLAYER, HUNT_FOOD
 * - Personality-based decision making (Collector, Hunter, Apex, Legendary)
 * - Predictive hunting with interception calculations
 * - Smart evasion patterns (zigzag, perpendicular turns)
 * - Collision avoidance with path scanning
 * - Boost decision making based on score
 */
function updateOfflineAIWorms(state: OfflineGameState, dt: number): void {
	// Build game state once for all AI worms
	const gameState = buildClientAIGameState(state);

	state.otherWorms.forEach((worm, index) => {
		const headPos = worm.getHeadPosition();
		const currentAngle = worm.getHeadRotation();
		const aiScore = worm.getSegmentCount() * GROWTH.SCORE_PER_SEGMENT;

		// Update think timer
		state.aiThinkTimers[index] = (state.aiThinkTimers[index] ?? 0) + dt * 1000;

		// Check if it's time to think (AI doesn't need frame-perfect decisions)
		if (state.aiThinkTimers[index] >= AI_BRAIN_THINK_INTERVAL_MS) {
			state.aiThinkTimers[index] = 0;

			// Build worm data for this specific AI
			const aiWormData: ClientAIWormData = {
				id: `ai_${index}`,
				index,
				isAI: true,
				alive: true,
				score: aiScore,
				angle: currentAngle,
				boosting: state.aiBoosting[index] ?? false,
				headX: headPos.x,
				headY: headPos.y,
				radius: worm.getRadius(),
				segments: worm.getSegmentPositions().map(seg => ({ x: seg.x, y: seg.y, radius: worm.getRadius() })),
				aiState: state.aiStates[index] ?? AIState.HUNT_FOOD,
				aiThinkTimer: 0,
				personality: state.aiPersonalities[index] ?? AI_PERSONALITIES.Hunter,
			};

			// Call AI brain for decision - use Leviathan brain in nightmare mode
			const decision = state.nightmareMode
				? LeviathanBrain.think(aiWormData, gameState, dt, DEFAULT_AI_CONFIG)
				: AIBrainClient.think(aiWormData, gameState, DEFAULT_AI_CONFIG);

			// Update AI state
			state.aiStates[index] = decision.newState;
			state.aiBoosting[index] = decision.sprinting;
			state.aiCrawling[index] = decision.crawling ?? false;

			// Apply turn rate limiting (smooth turning)
			// Crawling gets ENHANCED turn rate for sharper maneuvers
			const angleDiff = MathUtils.normalizeAngle(decision.targetAngle - currentAngle);
			const turnRateMultiplier = decision.crawling ? 1.3 : 1.0; // 30% faster turning when crawling
			const maxTurn = AI_BRAIN_TURN_RATE * turnRateMultiplier * dt;
			const clampedTurn = Math.max(-maxTurn, Math.min(maxTurn, angleDiff));
			const newAngle = currentAngle + clampedTurn;

			// Calculate speed: crawl < normal < sprint
			// Crawling = 0.5x speed but sharper turns
			// Sprinting = 2x speed
			let speed = AI_WORM_SPEED;
			if (decision.crawling) {
				speed = AI_WORM_SPEED * SLOW_CRAWL.SPEED_MULTIPLIER; // 0.5x
			} else if (decision.sprinting) {
				speed = AI_WORM_SPEED * AI_BRAIN_BOOST_MULTIPLIER; // 2x
			}

			// Apply boost visual effect
			worm.setBoostEffect(decision.sprinting);

			// Move in new direction
			const moveX = Math.cos(newAngle) * speed * dt;
			const moveY = Math.sin(newAngle) * speed * dt;

			worm.setDeltaTime(dt);
			worm.updateHeadPosition(headPos.x + moveX, headPos.y + moveY);
		} else {
			// Between think intervals: continue current movement
			let speed = AI_WORM_SPEED;
			if (state.aiCrawling[index]) {
				speed = AI_WORM_SPEED * SLOW_CRAWL.SPEED_MULTIPLIER;
			} else if (state.aiBoosting[index]) {
				speed = AI_WORM_SPEED * AI_BRAIN_BOOST_MULTIPLIER;
			}

			const moveX = Math.cos(currentAngle) * speed * dt;
			const moveY = Math.sin(currentAngle) * speed * dt;

			worm.setDeltaTime(dt);
			worm.updateHeadPosition(headPos.x + moveX, headPos.y + moveY);
		}

		// AI food collection (grows just like player - 1 segment per food)
		// Pickup radius scales with worm size: Head Radius + BASE_FOOD_PICKUP_OFFSET
		// Same formula for player and AI - fair for everyone
		const newHeadPos = worm.getHeadPosition();
		const aiPickupRadius = worm.getRadius() + BASE_FOOD_PICKUP_OFFSET;
		const aiNearbyFood = state.foodRenderer.getFoodNear(newHeadPos.x, newHeadPos.y, aiPickupRadius);
		if (aiNearbyFood.length > 0) {
			for (const food of aiNearbyFood) {
				state.foodRenderer.removeFood(food.id);
			}
			// Update segment count (1 segment per food eaten)
			const currentSegments = worm.getSegmentCount();
			const newSegmentCount = currentSegments + aiNearbyFood.length;
			worm.setSegmentCount(newSegmentCount);

			// Update radius based on new segment count
			const newRadius = calculateWormRadius(newSegmentCount);
			worm.setRadius(newRadius);

			// Track in state for collision and death drops
			if (state.aiSegmentCounts[index] !== undefined) {
				state.aiSegmentCounts[index] = newSegmentCount;
			}
		}

		// AI powerup collection (AI gains segments like player, powerup is removed)
		// This allows AI to compete for powerups and makes collecting them a racing priority
		const aiPowerupPickupRadius = worm.getRadius() + 20; // Same offset as player
		const aiNearbyPowerups = state.powerupRenderer.getPowerupsNear(newHeadPos.x, newHeadPos.y, aiPowerupPickupRadius);
		for (const powerup of aiNearbyPowerups) {
			// Remove the powerup from the world (AI claimed it)
			state.powerupRenderer.removePowerup(powerup.id);
			state.powerupSpawnTimes.delete(powerup.id);

			// Grant segment bonus to AI (same as player gets +5 segments per powerup)
			const currentSegments = worm.getSegmentCount();
			const newSegmentCount = currentSegments + POWERUP_SEGMENT_BONUS;
			worm.setSegmentCount(newSegmentCount);

			// Update radius based on new segment count
			const newRadius = calculateWormRadius(newSegmentCount);
			worm.setRadius(newRadius);

			// Track in state for collision and death drops
			if (state.aiSegmentCounts[index] !== undefined) {
				state.aiSegmentCounts[index] = newSegmentCount;
			}
		}
	});
}

/**
 * Handle player death
 */
function handlePlayerDeath(appState: AppState, killedBy: string | null, finalScore: number): void {
	const { deathUI, onlineState, pixiUI } = appState;

	if (!onlineState) return;

	// Stop input sending
	onlineState.inputManager.stop();

	// Hide powerup bar during death screen
	pixiUI.powerupBar.hide();
	pixiUI.powerupBar.clear();

	// Calculate earned points from kills (online is never nightmare mode)
	const earnedPoints = calculateEarnedPoints(onlineState.killCounts, false);

	// Show death UI with earned points
	deathUI.show(killedBy, finalScore, 1000, {
		kills: Object.values(onlineState.killCounts).reduce((a, b) => a + b, 0),
		earnedPoints,
	});
}

/**
 * Handle respawn request
 */
function handleRespawn(appState: AppState): void {
	const { deathUI, onlineState, offlineState, pixiUI, game } = appState;

	// Hide death UI
	deathUI.hide();

	// Show powerup bar again
	pixiUI.powerupBar.show();

	// Reset score display
	pixiUI.scoreDisplay.setScore(0, false);

	// Reset kill score display
	pixiUI.killScoreDisplay.reset();

	// Handle offline respawn
	if (offlineState) {
		// Reset player state
		offlineState.playerDead = false;
		offlineState.score = OFFLINE_PLAYER_SEGMENT_COUNT * GROWTH.SCORE_PER_SEGMENT;
		offlineState.segmentCount = OFFLINE_PLAYER_SEGMENT_COUNT;
		offlineState.currentAngle = 0;
		offlineState.growthAccumulator = 0;

		// Reset stats for new life
		offlineState.stats = {
			foodEaten: 0,
			timeAlive: 0,
			maxLength: OFFLINE_PLAYER_SEGMENT_COUNT,
			kills: 0,
		};

		// Reset kill counts for new life
		offlineState.killCounts = createEmptyKillCounts();

		// Find a safe spawn position away from all AI worms
		const MIN_SPAWN_DISTANCE = 400;
		let spawnX = 0;
		let spawnY = 0;
		let foundSafe = false;

		for (let attempt = 0; attempt < 50; attempt++) {
			// Try random positions, starting near center then expanding
			const angle = Math.random() * Math.PI * 2;
			const distance = attempt * 50; // Start at center, expand outward
			const testX = Math.cos(angle) * distance;
			const testY = Math.sin(angle) * distance;

			// Check if this position is safe from all AI worms
			let isSafe = true;
			for (const aiWorm of offlineState.otherWorms) {
				const aiHead = aiWorm.getHeadPosition();
				const dx = testX - aiHead.x;
				const dy = testY - aiHead.y;
				const dist = Math.sqrt(dx * dx + dy * dy);
				if (dist < MIN_SPAWN_DISTANCE) {
					isSafe = false;
					break;
				}
			}

			if (isSafe) {
				spawnX = testX;
				spawnY = testY;
				foundSafe = true;
				break;
			}
		}

		if (!foundSafe) {
			// Fallback: spawn at center anyway (rare case)
			console.warn('Could not find safe spawn position, using center');
		}

		// Reset player worm position and size
		const initialRadius = calculateWormRadius(OFFLINE_PLAYER_SEGMENT_COUNT);
		offlineState.playerWorm.setSegmentCount(OFFLINE_PLAYER_SEGMENT_COUNT);
		offlineState.playerWorm.setRadius(initialRadius);
		offlineState.playerWorm.updateHeadPosition(spawnX, spawnY);
		offlineState.playerWorm.setVisible(true);

		// Snap camera to spawn
		game.camera.snapTo(spawnX, spawnY);
		game.camera.playStartAnimation();

		// Update score display
		pixiUI.scoreDisplay.setScore(offlineState.score, false);

		// Show revenge intel arrow if killed by an AI worm
		if (offlineState.lastKillerIndex !== null) {
			const killerIndex = offlineState.lastKillerIndex;
			pixiUI.revengeIntelArrow.show(() => {
				// Get killer's current position
				const killer = offlineState.otherWorms[killerIndex];
				if (killer) {
					return killer.getHeadPosition();
				}
				return null; // Killer no longer exists
			});
			offlineState.lastKillerIndex = null; // Clear after using
		}

		return;
	}

	// Handle online respawn
	if (onlineState) {
		// Reset kill counts for new life
		onlineState.killCounts = createEmptyKillCounts();
		onlineState.combo = { count: 0, timeSinceKill: COMBO_TIME_WINDOW + 1 };

		// Send respawn message to server with skin data preserved
		onlineState.networkManager.send('respawn', {
			name: onlineState.playerName,
			skin: onlineState.playerSkin,
		});

		// Restart input sending
		onlineState.inputManager.start(onlineState.networkManager);

		// Show revenge intel arrow if killed by another player
		if (onlineState.lastKillerId) {
			const killerId = onlineState.lastKillerId;
			pixiUI.revengeIntelArrow.show(() => {
				// Get killer's current position via stateSync
				const killerRenderer = onlineState.stateSync.getWormRenderer(killerId);
				if (killerRenderer) {
					return killerRenderer.getHeadPosition();
				}
				return null; // Killer no longer exists
			});
			onlineState.lastKillerId = null; // Clear after using
		}
	}
}

/**
 * Handle leave game (from ESC menu)
 * Player dies, drops points, gets 5 second fogged spectate view
 */
function handleLeaveGame(appState: AppState): void {
	const { escMenu, game, onlineState, offlineState, pixiUI } = appState;

	// Hide ESC menu
	escMenu.hide();

	// Start spectating mode
	appState.spectatingAfterLeave = true;
	appState.spectateTimer = 5; // 5 seconds

	// Send suicide message to server (online mode)
	if (onlineState) {
		onlineState.networkManager.send(MessageType.SUICIDE, {});
	}

	// Kill player in offline mode
	if (offlineState && !offlineState.playerDead) {
		offlineState.playerDead = true;
		offlineState.playerWorm.setVisible(false);
		// Spawn food from player body
		const segments = offlineState.playerWorm.getBodyPoints();
		segments.forEach((seg) => {
			offlineState.foodRenderer.addFood({
				id: `leave-${Date.now()}-${Math.random()}`,
				x: seg.x + (Math.random() - 0.5) * 10,
				y: seg.y + (Math.random() - 0.5) * 10,
				radius: FOOD_RADIUS,
				color: 0xff6b6b,
			});
		});
	}

	// Hide UI components (but keep game rendering)
	pixiUI.powerupBar.hide();
	pixiUI.powerupBar.clear();
	pixiUI.scoreDisplay.hide();

	// Create spectate fog overlay
	const spectateOverlay = new Graphics();
	spectateOverlay.rect(0, 0, game.app.screen.width, game.app.screen.height);
	spectateOverlay.fill({ color: 0x0a0a14, alpha: 0.6 });
	spectateOverlay.zIndex = 9999;
	game.uiContainer.addChild(spectateOverlay);

	// Create "Leaving game..." text
	const leaveText = document.createElement('div');
	leaveText.id = 'leave-spectate-text';
	leaveText.innerHTML = `
    <div style="
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      text-align: center;
      color: #fff;
      font-family: 'Segoe UI', Arial, sans-serif;
      z-index: 10000;
    ">
      <div style="font-size: 24px; margin-bottom: 10px; opacity: 0.9;">Leaving game...</div>
      <div id="leave-countdown" style="font-size: 48px; color: #4ecdc4; margin-top: 20px; font-weight: bold;">5</div>
      <div style="font-size: 14px; color: #666; margin-top: 15px;">Press ESC to skip</div>
    </div>
  `;
	document.body.appendChild(leaveText);

	// Countdown timer
	let countdown = 5;
	const countdownEl = document.getElementById('leave-countdown');

	// Function to clean up and return to menu
	const cleanupAndQuit = () => {
		clearInterval(countdownInterval);
		document.removeEventListener('keydown', escHandler);

		// Remove overlay and text
		game.uiContainer.removeChild(spectateOverlay);
		spectateOverlay.destroy();
		leaveText.remove();

		// Reset spectating state
		appState.spectatingAfterLeave = false;

		// Return to menu
		handleQuit(appState);
	};

	// ESC key handler to skip countdown
	const escHandler = (e: KeyboardEvent) => {
		if (e.key === 'Escape') {
			e.preventDefault();
			cleanupAndQuit();
		}
	};
	document.addEventListener('keydown', escHandler);

	const countdownInterval = setInterval(() => {
		countdown--;
		if (countdownEl) {
			countdownEl.textContent = countdown.toString();
		}
		appState.spectateTimer = countdown;

		if (countdown <= 0) {
			cleanupAndQuit();
		}
	}, 1000);
}

/**
 * Handle quit to menu
 */
function handleQuit(appState: AppState): void {
	const { connectionUI, deathUI, leaderboardUI, pixiUI, escMenu } = appState;

	// Hide death UI
	deathUI.hide();
	escMenu.hide();
	leaderboardUI.hide();

	// Hide all PixiJS UI components
	pixiUI.minimap.hide();
	pixiUI.leaderboard.hide();
	pixiUI.powerupBar.hide();
	pixiUI.powerupBar.clear();
	pixiUI.scoreDisplay.hide();

	// Clean up current game
	cleanupCurrentGame(appState);

	// Set mode to menu
	appState.mode = GameMode.MENU;

	// Show connection UI
	connectionUI.show();
}

/**
 * Clean up current game state
 */
function cleanupCurrentGame(appState: AppState): void {
	const { game } = appState;

	// Clean up online state
	if (appState.onlineState) {
		const { networkManager, stateSync, inputManager, foodRenderer, powerupRenderer, particleSystem, textPopupSystem, trailSystem } = appState.onlineState;

		inputManager.destroy();
		stateSync.destroy();
		networkManager.destroy();

		// Remove food and powerup renderers
		const foodLayer = game.getLayer(GameLayer.FOOD);
		foodLayer.removeChild(foodRenderer.container);
		foodRenderer.destroy();
		foodLayer.removeChild(powerupRenderer.container);
		powerupRenderer.destroy();

		// Clean up particle, text, and trail systems
		const wormsLayer = game.getLayer(GameLayer.WORMS);
		wormsLayer.removeChild(particleSystem.container);
		particleSystem.destroy();
		wormsLayer.removeChild(textPopupSystem.container);
		textPopupSystem.destroy();
		wormsLayer.removeChild(trailSystem.container);
		trailSystem.destroy();

		appState.onlineState = null;
	}

	// Clean up offline state
	if (appState.offlineState) {
		const { playerWorm, otherWorms, foodRenderer, deathEffects, dyingWorms, powerupRenderer, eatingRadiusGraphics } = appState.offlineState;

		// Remove worms
		const wormsLayer = game.getLayer(GameLayer.WORMS);
		wormsLayer.removeChild(playerWorm.container);
		playerWorm.destroy();

		otherWorms.forEach((worm) => {
			wormsLayer.removeChild(worm.container);
			worm.destroy();
		});

		// Remove dying worms
		dyingWorms.forEach((worm) => {
			wormsLayer.removeChild(worm.container);
			worm.destroy();
		});

		// Remove death effects
		wormsLayer.removeChild(deathEffects.getContainer());
		deathEffects.destroy();

		// Remove eating radius graphics
		wormsLayer.removeChild(eatingRadiusGraphics);
		eatingRadiusGraphics.destroy();

		// Remove food and powerups
		const foodLayer = game.getLayer(GameLayer.FOOD);
		foodLayer.removeChild(foodRenderer.container);
		foodRenderer.destroy();
		foodLayer.removeChild(powerupRenderer.container);
		powerupRenderer.destroy();

		appState.offlineState = null;
	}
}

/**
 * Setup base UI elements and keyboard controls
 */
function setupBaseUI(game: Game): void {
	// Keyboard zoom controls
	window.addEventListener('keydown', (e: KeyboardEvent) => {
		if (e.key === '=' || e.key === '+') {
			game.camera.adjustZoom(0.1);
		} else if (e.key === '-' || e.key === '_') {
			game.camera.adjustZoom(-0.1);
		} else if (e.key === 'm' || e.key === 'M') {
			// Toggle sound mute
			const soundManager = SoundManager.getInstance();
			const muted = soundManager.toggleMute();
			showMuteIndicator(muted);
		}
	});
}

/**
 * Show a temporary visual indicator when mute state changes
 */
function showMuteIndicator(muted: boolean): void {
	// Remove existing indicator if any
	const existing = document.getElementById('mute-indicator');
	if (existing) existing.remove();

	// Create indicator
	const indicator = document.createElement('div');
	indicator.id = 'mute-indicator';
	indicator.innerHTML = muted ? '🔇 Sound Off' : '🔊 Sound On';
	indicator.style.cssText = `
    position: fixed;
    top: 20px;
    left: 50%;
    transform: translateX(-50%);
    background: rgba(0, 0, 0, 0.8);
    color: ${muted ? '#ff6b6b' : '#4ecdc4'};
    padding: 12px 24px;
    border-radius: 8px;
    font-family: Arial, sans-serif;
    font-size: 16px;
    font-weight: bold;
    z-index: 9999;
    pointer-events: none;
    animation: muteIndicatorFade 1.5s ease-out forwards;
  `;

	// Add animation keyframes if not present
	if (!document.getElementById('mute-indicator-styles')) {
		const style = document.createElement('style');
		style.id = 'mute-indicator-styles';
		style.textContent = `
      @keyframes muteIndicatorFade {
        0% { opacity: 1; transform: translateX(-50%) translateY(0); }
        70% { opacity: 1; }
        100% { opacity: 0; transform: translateX(-50%) translateY(-10px); }
      }
    `;
		document.head.appendChild(style);
	}

	document.body.appendChild(indicator);

	// Remove after animation
	setTimeout(() => indicator.remove(), 1500);
}

// Global logger for error handling
const globalLogger: Logger = createConsoleLogger({ component: 'Global' }, 'info');

// Global error handler for uncaught exceptions
window.onerror = (message, source, lineno, colno, error): boolean => {
	globalLogger.error('Uncaught exception', {
		message: String(message),
		source,
		line: lineno,
		column: colno,
		error: error?.stack ?? String(error),
	});
	return false; // Don't prevent default handling
};

// Global handler for unhandled promise rejections
window.onunhandledrejection = (event: PromiseRejectionEvent): void => {
	globalLogger.error('Unhandled promise rejection', {
		reason: event.reason?.message ?? String(event.reason),
		stack: event.reason?.stack,
	});
};

// Start the application
main().catch((error) => {
	globalLogger.fatal('Failed to initialize WormCosmos client', {
		error: error?.message ?? String(error),
		stack: error?.stack,
	});
	document.body.innerHTML = `
    <div style="color: white; font-family: Arial; padding: 20px;">
      <h1>Failed to initialize game</h1>
      <pre>${error.message}</pre>
    </div>
  `;
});
